import { Axis } from "./core/elements/axis"
import { Format } from "./core/elements/format"
import { Point, Rectangle } from "./core/primitives"
import { Palette, RainbowPalette } from "./palette"
import { Animation, Marks } from "./tools/index"
import { pointInLine } from "./utils"

function parseText(data,text) {
  data.values=[];
  data.labels=[];

  var v=text.split("\n"), len=v.length;

  if (len>0) {
    for(var t=0; t<len; t++) {
      var s=v[t], fields=s.split(","), anyNumber=false;

      for(var f=0; f<fields.length; f++)
      {
        s=parseFloat(fields[f]);
        if (s && (!isNaN(s))) {
           anyNumber=true;
           break;
        }
      }

      if (anyNumber) {
        if (fields.length==1) {
          s=parseFloat(s);
          if (isNaN(s)) s=0;
        }
        else
        if (fields.length==2) {
          s=parseFloat(fields[0]);
          if (isNaN(s)) s=0;
          data.labels[t]=fields[1];
        }
        else
        if (fields.length==3) {
          s=parseFloat(fields[1]);
          if (isNaN(s)) s=0;

          data.labels[t]=fields[2];
        }

        data.values[t]=s;
      }
      else
        data.values[t]=0;

    }
  }
}

/**
 * @returns {Number} Returns the integer part of value, without decimals, rounded to lower.
 */
function trunc(value) {
	return value | 0
}

function isArray(obj) {
	return obj && obj.constructor == Array
}

/**
 * removes nulls that Math.max/min.apply doesn't handle correctly
 */
function removeEmptyArrayElements(arr) {
	if (!isArray(arr)) {
		return arr
	} else {
		return arr.filter(elem => elem !== null).map(removeEmptyArrayElements)
	}
}

/**
 * @returns {Number} Returns the sum of all absolute values in the array or typed-array parameter.
 * @param {Array|ArrayBuffer} a The array or typed-array to do absolute sum.
 */
function ArraySumAbs(a) {
	var sum = 0,
		len = a.length
	while (len--) sum += a[len] > 0 ? a[len] : -a[len]
	return sum
}

/**
 * @returns {Number} Returns the maximum value in the array or typed-array parameter.
 * @param {Array|ArrayBuffer} a The array or typed-array of numbers.
 */
function ArrayMax(a) {
	var arr = removeEmptyArrayElements(a)
	var max = -Number.MAX_VALUE
	arr.forEach(e => {
		if (max < e) {
			max = e
		}
	})
	if (Object.prototype.toString.call(max) === "[object Date]") {
		max = max.getTime()
	}
	return max
}
/**
 * @returns {Number} Returns the minimum value in the array or typed-array parameter.
 * @param {Array|ArrayBuffer} a The array or typed-array of numbers.
 */
function ArrayMin(a) {
	var arr = removeEmptyArrayElements(a)
	var min = Number.MAX_VALUE
	arr.forEach(e => {
		if (min > e) {
			min = e
		}
	})
	if (Object.prototype.toString.call(min) === "[object Date]") {
		min = min.getTime()
	}
	return min
}

/**
 * @constructor
 * @class Base abstract class to define a series of data
 * @param {Object|Tee.Chart|Number[]} o An array of numbers, or a chart or datasource object.
 * @property {Tee.Chart} chart The parent chart this Series object belongs to.
 * @property {Number[]} data.values Array of numbers as main series data.
 * @property {String[]} data.labels Array of strings used to display at axis labels, legend and marks.
 * @property {Format} format Visual properties to display series data.
 * @property {Boolean} [visible=true] Determines if this series will be displayed or not.
 * @property {String} [cursor="default"] Defines the mouse cursor to show when mouse is over a series point.
 * @property {Object} data Contains all series data values, labels, etc.
 * @property {Tee.Series.Marks} marks Displays annotations near series points.
 * @property {Boolean} [colorEach="auto"] Paints points using series fill color, or each point with a different color
 * from series palette or chart palette color array.
 * @property {String} [horizAxis="bottom"] Defines the horizontal axis associated with this series.
 * @property {String} [vertAxis="left"] Defines the horizontal axis associated with this series.
 */
let Series = function (o, o2) {
	this.chart = null
	this.data = { values: [], labels: [], source: null }

	this.sortedOptions = {
		sortedDrawAnimation: new Animation(),
		sortedValues: [],
		sortedValuesIndices: [],
		originalValues: [],
		sortedLabels: [],
		originalLabels: [],
		sorted: false,
		ascending: true,
		sorting: false,
		sortingAnimationType: "verticalchange"
	}
	this.yMandatory = true
	this.horizAxis = "bottom"
	this.vertAxis = "left"
	this.legend = { visible: true }
	this.legendStrokeColor = false

	this.sequential = true

	var f = (this.format = new Format(this.chart)),
		ho = (this.hover = new Format(this.chart)),
		s = ho.shadow
	this.sortedOptions.sortedDrawAnimation.duration = 500
	this.sortedOptions.sortedDrawAnimation.mode = "linear"
	f.fill = ""
	f.stroke.fill = ""
	this.visible = true

	// Hover
	ho.stroke.size = 0.3
	ho.fill = ""
	ho.stroke.fill = "red"

	s.visible = true
	s.blur = 10
	s.width = 0
	s.height = 0

	this.cursor = "default"
	this.over = -1

	this.marks = new Marks(this, this.chart)

	this.palette = new Palette()
	this.paletteName = "opera"
	this.themeName = "default"

	this.colorEach = "auto"
	this.useAxes = true
	this.decimals = 2

	this._paintAxes = true
	this._paintWalls = true

	this.sortValues = function () {
		this.sortedOptions.sorting = true
		var indices = [],
			values = this.data.values,
			ascending = this.sortedOptions.ascending
		this.sortedOptions.sortedLabels = []
		for (var i = 0; i < this.data.values.length; i++) {
			indices.push(i)
		}
		indices.sort((a, b) => (ascending ? values[a] - values[b] : values[b] - values[a]))
		this.sortedOptions.originalValues = this.data.values.slice()
		this.sortedOptions.originalLabels = this.data.labels.slice()
		for (var i = 0; i < indices.length; i++) {
			this.sortedOptions.sortedLabels.push(this.data.labels[indices[i]] ? this.data.labels[indices[i]] : indices[i])
		}
		this.sortedOptions.sortedValuesIndices = indices
		this.sortedOptions.sortedValues = this.data.values.slice().sort((a, b) => (ascending ? a - b : b - a))
		this.sortedOptions.sorting = false
		return this.sortedOptions.sortedValues.slice()
	}

	this.drawSortedValues = function (sorted) {
		var animation = this.sortedOptions.sortedDrawAnimation
		if (this.sortedOptions.sorted != sorted && !this.sortedOptions.sorting && animation && !animation.running) {
			this.sortedOptions.sorted = sorted
			var data = this.data
			if (sorted) this.sortValues()
			var prevValues = sorted ? this.sortedOptions.originalValues.slice() : this.sortedOptions.sortedValues.slice()
			var values = this.data.values
			var endValues = sorted ? this.sortedOptions.sortedValues.slice() : this.sortedOptions.originalValues.slice()

			if (!(values.length == endValues.length && values.every((v, i) => v === endValues[i]))) {
				animation.chart = this.chart

				animation.doStep = f => {
					if (f < 1) {
						for (var i = 0; i < prevValues.length; i++) {
							values[i] = prevValues[i] + (endValues[i] - prevValues[i]) * f
						}
					}
				}

				animation.onstop = () => {
					for (var i = 0; i < prevValues.length; i++) {
						values[i] = endValues[i]
					}
					animation.running = false
				}

				animation.onstart = () => {
					animation.running = true
				}
				animation.animate()
			}
			this.data.labels = sorted ? this.sortedOptions.sortedLabels.slice() : this.sortedOptions.originalLabels.slice()
			this.chart.draw()
		}
	}

	this.init = function (o, o2) {
		if (typeof o === "object") {
			if (o) {
				if (o instanceof Array) {
					this.data.values = o

					if (o2 instanceof Array) this.data.labels = o2
				} else if (o instanceof Tee.Chart) {
					this.chart = o
					if (o2 instanceof Array) this.data.values = o2
				} else {
					this.data.source = o
					this.refresh()
				}
			}
		}
	}

	this.init(o, o2)

	/**
	 * @returns {String} Returns the color of index point in series, using series palette or chart palette.
	 */
	this.getFill = function (index, f) {
		var p = this.palette,
			c = p && p.colors ? p.get(index) : null
		if (c === null) return this.isColorEach || !f ? this.chart.palette.get(index) : f.fill
		else return c
	}

	/**
	 * @returns {boolean} Returns true when the index'th series value is null and should not be painted.
	 */
	this.isNull = function (index) {
		return this.data.values[index] === null
	}

	/**
	 * @returns {CanvasGradient} Returns a canvas gradient using color, or color if gradient is not visible.
	 */
	this.getFillStyle = (r, color) => (f.gradient.visible ? f.gradient.create(r, color) : color)

	this.title = ""

	this.titleText = function (index) {
		return this.title || "Series " + index.toString()
	}

	this.refresh = function (failure) {
		if (this.data.source) {
			if (this.data.source instanceof HTMLTextAreaElement) {
				parseText(this.data, this.data.source.value)

				if (this.chart) this.chart.draw()
			} else if (this.data.source instanceof HTMLInputElement) {
				Tee.doHttpRequest(
					this,
					this.data.source.value,
					(target, data) => {
						parseText(target.data, data)
						target.chart.draw()
					},
					function (status, statusText) {
						if (failure) failure(this, status, statusText)
					}
				)
			} else if (failure) failure(this)
		} else if (this.data.xml) {
			parseXML(this, this.data.xml)
			this.chart.draw()
		} else if (this.data.json) {
			parseJSON(this, this.data.json)
			this.chart.draw()
		}
	}

	/**
	 * @returns {String} Returns the series index'th data label, or the value if no label exists at that index.
	 */
	this.valueOrLabel = function (index) {
		var s = this.data.labels[index]

		if (!s || s === "") s = this.valueText(index)

		return s
	}

	/**
	 * @returns {String} Returns a percentual representation of the series index'th value, on total of series values.
	 */
	this.toPercent = function (index) {
		var v = this.data.values
		return ((100 * Math.abs(v[index])) / ArraySumAbs(v)).toFixed(this.decimals) + " %"
	}

	/**
	 * @returns {String} Returns the text string to show at series marks, for a given series point index.
	 */
	this.markText = function (index) {
		var m = this.marks,
			res = this.dataText(index, m.style, false)
		return m.ongettext ? m.ongettext(this, index, res) : res
	}

	/**
	 * @returns {Boolean} Returns if series is associated to axis, either horizontal or vertical.
	 */
	this.associatedToAxis = function (axis) {
		if (axis.horizontal) return this.horizAxis == "both" || this._horizAxis == axis
		else return this.vertAxis == "both" || this._vertAxis == axis
	}

	this.bounds = function (r) {
		var h = this._horizAxis,
			v = this._vertAxis

		r.x = h.calc(this.minXValue())
		r.width = h.calc(this.maxXValue()) - r.x

		r.y = v.calc(this.maxYValue())
		r.height = v.calc(this.minYValue()) - r.y
	}

	this.calcStack = function (index, p, value) {
		var sum = this.pointOrigin(index, false) + value,
			tmp,
			a = this.mandatoryAxis

		p.x = this.notmandatory.calc(this.data.x ? this.data.x[index] : index)

		if (this.isStack100) {
			tmp = this.pointOrigin(index, true)
			p.y = tmp === 0 ? a.endPos : a.calc((sum * 100.0) / tmp)
		} else p.y = a.calc(sum)

		if (!this.yMandatory) {
			tmp = p.x
			p.x = p.y
			p.y = tmp
		}
	}

	/**
	 * @returns {Number} Returns the sum of all previous visible series index'th value, for stacking.
	 */
	this.pointOrigin = function (index, sumAll) {
		var res = 0,
			t,
			s,
			li = this.chart.series.items,
			v,
			tmp

		for (t = 0; t < li.length; t++) {
			s = li[t]

			if (!sumAll && s == this) break
			else if (s.stacked != "no") {
				v = s.data.values

				if (s.visible && s.constructor == this.constructor && v.length > index) {
					tmp = v[index]

					// Protect against undefined (NaN)
					if (tmp !== undefined) res += sumAll && tmp < 0 ? -tmp : tmp
				}
			}
		}

		return res
	}

	this.doHover = function (index) {
		var o = this.chart
		if (index != this.over) {
			if (o.onhover) o.onhover(this, index)

			this.over = index

			if (this.hover.enabled)
				requestAnimationFrame(() => {
					o.draw()
				})
		}
	}
}

/*
	* @private
	*/
Series.prototype.initZ = function (index, total) {
	var f = this.format
	f.z = index / total
	f.depth = 1 / total
	this.marks.format.z = f.z + f.depth * 0.5
}

/*
	* @private
	*/
Series.prototype.setChart = (series, chart) => {
	series.chart = chart

	series.recalcAxes()

	series.format.setChart(chart)
	series.marks.setChart(chart)
	series.hover.setChart(chart)
}

Series.prototype.calc = function (index, p) {
	var d = this.data,
		x = this.notmandatory.calc(d.x ? d.x[index] : index),
		y = this.mandatoryAxis.calc(d.values[index])

	p.x = this.yMandatory ? x : y
	p.y = this.yMandatory ? y : x
}

Series.prototype.recalcAxes = function () {
	var a = this.chart.axes

	if (this.horizAxis instanceof Axis) this._horizAxis = this.horizAxis
	else this._horizAxis = this.horizAxis == "top" ? a.top : a.bottom

	if (this.vertAxis instanceof Axis) this._vertAxis = this.vertAxis
	else this._vertAxis = this.vertAxis == "right" ? a.right : a.left

	this.mandatoryAxis = this.yMandatory ? this._vertAxis : this._horizAxis
	this.notmandatory = this.yMandatory ? this._horizAxis : this._vertAxis
}

// Pending for solution (gauges.bounds) :
Series.prototype.getRect = () => new Rectangle()

Series.prototype.clicked = () => -1

Series.prototype.fixedFloatToLocal = function (value, decimals) {
	var fixed = value.toFixed(decimals)
	var localeVal = value.toLocaleString(this.chart.language)

	if (fixed.indexOf(".") != -1) {
		var n = 1.1
		n = n.toLocaleString(this.chart.language).substring(1, 2)
		var fractions = fixed.substring(fixed.indexOf(".") + 1) //zero based idx
		return (
			localeVal.substring(0, localeVal.indexOf(n) == -1 ? localeVal.length : localeVal.indexOf(n)) + n + fractions
		)
	} else return value
}

/**
 * @returns {String} Returns the text string for a given series point index value.
 */
Series.prototype.valueText = function (index) {
	var vv = this.data._old || this.data.values,
		d = vv[index]

	if (d) {
		if (d instanceof Date) return d.format ? d.format(this.dateFormat) : d.toString()
		else if (this.valueFormat) return d.toLocaleString(this.chart.language)
		else if (trunc(d) == d) return this.fixedFloatToLocal(d, 0).toString()
		else return this.fixedFloatToLocal(d, this.decimals).toString()
	} else return "0"
}

Series.prototype.labelOrTitle = function (index) {
	return this.data.labels[index] || this.title
}

Series.prototype.mousedown = () => false

Series.prototype.mousemove = function (p) {
	if (this.hover.enabled || this.cursor != "default") {
		var tmp = this.clicked(p)

		this.doHover(tmp)

		if (this.cursor != "default" && tmp != -1) {
			if (!this.chart.newCursor) this.chart.newCursor = this.cursor
			return
		}
	}

	var m = this.marks

	if (m.visible) {
		var len = this.data.values.length,
			p2 = new Point(),
			t

		for (t = 0; t < len; t += m.drawEvery)
			if (!this.isNull(t)) {
				this.markPos(t, p2)

				if (m.canDraw(p2.x, p2.y, t)) {
					if (m.bounds.contains(p)) {
						this.doHover(t)
						break
					}
				}
			}
	}
}

Series.prototype.mouseout = () => {}

Series.prototype.markPos = function (t, p) {
	this.calc(t, p)
	return false
}

Series.prototype.drawMarks = function () {
	var len = this.data.values.length,
		p = new Point(),
		t,
		inv

	for (t = 0; t < len; t += this.marks.drawEvery)
		if (!this.isNull(t)) {
			inv = this.markPos(t, p)
			this.marks.drawMark(p.x, p.y, t, inv)
		}
}

Series.prototype.horizMargins = () => {}
Series.prototype.vertMargins = () => {}

/**
 * @returns {Number} Returns the minimum value of series x values, or zero if no x values exist.
 */
Series.prototype.minXValue = function () {
	return this.data.x && this.data.x.length > 0 ? ArrayMin(this.data.x) : 0
}

/**
 * @returns {Number} Returns the minimum value of series data values, or zero if no values exist.
 */
Series.prototype.minYValue = function () {
	var v = this.data.values
	return v.length > 0 ? ArrayMin(v) : 0
}

/**
 * @returns {Number} Returns the maximum value of series x values, or data length minus one, if no x values exist.
 */
Series.prototype.maxXValue = function () {
	if (this.data.x) return this.data.x.length > 0 ? ArrayMax(this.data.x) : 0
	else {
		var len = this.data.values.length
		return len === 0 ? 0 : len - 1
	}
}

/**
 * @returns {Number} Returns the maximum value of series values, or zero if no values exist.
 */
Series.prototype.maxYValue = function () {
	var v = this.data.values,
		l = v.length,
		t,
		value,
		res

	if (l > 0) {
		res = ArrayMax(v)

		if (res !== res) {
			// isNan, protect against
			for (t = 0; t < l; t++) {
				value = v[t]

				if (value !== undefined) {
					if (res !== res) res = value
					else if (value > res) res = value
				}
			}
		}

		return res === res ? res : 0
	} else return 0
}

Series.prototype.calcColorEach = function () {
	this.isColorEach = this.colorEach == "yes"
}

/**
 * @returns {Number} Returns the maximum of all series values, or sum of all stacked values.
 */
Series.prototype.stackMaxValue = function () {
	if (this.stacked == "100") return 100
	else {
		var temp = Series.prototype.maxYValue

		if (this.stacked == "no") return temp.call(this)
		else {
			var res = temp.call(this),
				v = this.data.values,
				len = v.length,
				value

			while (len--) {
				value = v[len]
				if (value === undefined) value = 0
				res = Math.max(res, this.pointOrigin(len, false) + value)
			}

			return res
		}
	}
}

/**
 * @returns {String} Returns the text string to show for a given series point index.
 * @param {Number} index The point position in series data array.
 * @param {String} style Defines how text is returned: "auto", "value", "percent", "percentlabel",
 * "valuelabel", "label", "index", "labelvalue", "labelpercent", "valuepercent"
 */
Series.prototype.dataText = function (index, style, title, asArray) {
	function calcRet(a, b) {
		if (asArray) return l ? [a, b] : [a, ""]
		else return a + (b ? " " + b : "")
	}

	var l = title ? this.labelOrTitle(index) : this.data.labels[index]

	if (style == "value") return this.valueText(index)
	else if (style == "percent") return this.toPercent(index)
	else if (style == "percentlabel") return calcRet(this.toPercent(index), l)
	else if (style == "valuelabel" || style == "auto") return calcRet(this.valueText(index), l)
	else if (style == "label") return l || ""
	else if (style == "index") return index.toFixed(0)
	else if (style == "labelvalue") return calcRet(l, this.valueText(index))
	else if (style == "labelpercent") return calcRet(l, this.toPercent(index))
	else if (style == "valuepercent") return this.valueText(index) + " " + this.toPercent(index)
	else return this.valueOrLabel(index)
}

/**
 * @returns {String} Returns the text string to show for a given series point index.
 * @param {Number} index The point position in series data array.
 * @param {String} style Defines how text is returned: "auto", "value", "percent", "percentlabel",
 * "valuelabel", "label", "index", "labelvalue", "labelpercent", "valuepercent"
 */
Series.prototype.legendText = Series.prototype.dataText

/**
 * @returns {Number} Returns the number of series data values.
 */
Series.prototype.count = function () {
	return this.data.values.length
}

/**
 * @returns {Number} Returns the number of items to show at legend.
 */
Series.prototype.legendCount = function () {
	return this.count()
}

/**
 * @returns {Color} Returns the color of index'th legend symbol.
 */
Series.prototype.legendColor = function (index) {
	return this.isColorEach && index != -1
		? this.getFill(index)
		: this.legendStrokeColor
			? this.format.stroke.fill
			: this.format.fill
}

Series.prototype.addRandom = function (count, range, x) {
	if (!range) range = 1000
	if (!count) count = 5

	var d = this.data
	d.values.length = count

	if (x) d.x = new Array(count)

	if (count > 0) {
		d.values[0] = Math.random() * range

		if (x) d.x[0] = Math.random() * range

		for (var t = 1; t < count; t++) {
			d.values[t] = d.values[t - 1] + Math.random() * range - range * 0.5
			if (x) d.x[t] = Math.random() * range
		}
	}

	return this
}

/**
 * @returns {Array} Returns an array of series data indices sorted according to sortBy parameter.
 */
Series.prototype.doSort = function (sortBy, ascending) {
	if (sortBy == "none") return null
	else {
		var d = this.data.values,
			len = d.length,
			sorted = new Array(len),
			t = 0
		for (; t < len; t++) sorted[t] = t

		if (sortBy == "labels") {
			d = this.data.labels

			var A,
				B,
				before = ascending ? -1 : 1,
				after = ascending ? 1 : -1

			sorted.sort((a, b) => {
				A = d[a].toLowerCase()
				B = d[b].toLowerCase()
				return A < B ? before : A == B ? 0 : after
			})
		} else sorted.sort(ascending ? (a, b) => d[a] - d[b] : (a, b) => d[b] - d[a])

		return sorted
	}
}

/**
 * @memberOf Tee.Chart
 * @constructor
 * @class Contains a list of chart series objects
 * @param {Tee.Chart} chart The parent chart this list of series belongs to.
 * @property {Series[]} items The array containing series instances.
 */
function SeriesList(chart) {
	this.chart = chart
	this.items = []

	/**
	 * @returns {Number} Returns the total number of series in chart, visible or not.
	 */
	this.count = function () {
		return this.items.length
	}

	/**
	 * @returns {Boolean} Returns if {@link Point} p parameter is over any series point.
	 */
	this.clicked = function (p) {
		var done = false

		this.each(s => {
			if (s.visible && s.onclick) {
				var index = s.clicked(p)
				if (index !== -1) done = s.onclick(s, index, p.x, p.y)
			}
		})

		return done
	}

	this.mousedown = function (event) {
		for (var t = 0, s, len = this.items.length, done = false; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.visible) {
				if (s.mousedown(event)) done = true
			}
		}

		return done
	}

	this.mousemove = function (p) {
		for (var t = 0, s, len = this.items.length; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.visible) s.mousemove(p)
		}

		/*
	var len=this.items.length, s;

	while(len--) {
		s=this.items[len];
		if (s.visible)
				s.mousemove(p);
	}
	*/
	}

	this.mouseout = function () {
		this.each(s => {
			if (s.visible) s.mouseout()
		})
	}

	/**
	 * Counts how many visible series exist of the same class type.
	 * @returns {Number} Returns the number of visible series in chart of the same type as this.
	 */
	this.visibleCount = function (s, c, res) {
		var r = 0,
			it = this.items,
			len = it.length,
			i

		while ((len--, (i = it[len])))
			if (i.visible && (!c || i instanceof c)) {
				if (res && i == s) res.index = r
				r++
			}
		if (res) {
			res.total = r
			res.index = r - 1 - res.index
		}
		return r
	}

	this.beforeDraw = function () {
		this.each(s => {
			if (s.useAxes) s.recalcAxes()

			s.calcColorEach()
		})
	}

	/**
	 * @returns {Boolean} Returns if any visible series in chart needs axes to be represented.
	 */
	this.anyUsesAxes = function () {
		var len = this.items.length,
			s
		while (len--) {
			s = this.items[len]
			if (s.visible && s.useAxes) return true
		}

		return false
	}

	/**
	 * @returns {Tee.Series} Returns the first visible series in chart, or null if any.
	 */
	this.firstVisible = function () {
		for (var t = 0, s, len = this.items.length; t < len; t++) {
			s = this.items[t]
			if (!s) break
			if (s.visible) return s
		}
		return null
	}

	/**
	 * Calculates the maximum amount of vertical margins in pixels from all series.
	 * @returns {Point} Returns the maximum top/bottom distance in pixels that all series need to be separated from axes.
	 */
	this.vertMargins = function () {
		var result,
			li = this.items,
			len = li.length,
			s,
			t

		if (len > 0) {
			result = { x: 0, y: 0 }
			s = { x: 0, y: 0 }
			li[0].vertMargins(result)

			for (t = 1; t < len; t++) {
				if (li[t].data.values.length > 0) {
					s.x = s.y = 0
					li[t].vertMargins(s)
					if (s.x > result.x) result.x = s.x
					if (s.y > result.y) result.y = s.y
				}
			}
		}
		return result
	}

	/**
	 * Calculates the maximum amount of horizontal margins in pixels from all series
	 * @returns {Point} Returns the maximum left/right distance in pixels that all series need to be separated from axes.
	 */
	this.horizMargins = function () {
		var result,
			li = this.items,
			len = li.length,
			s,
			t

		if (len > 0) {
			result = { x: 0, y: 0 }
			s = { x: 0, y: 0 }
			li[0].horizMargins(result)

			for (t = 1; t < len; t++) {
				if (li[t].data.values.length > 0) {
					s.x = s.y = 0
					li[t].horizMargins(s)

					if (s.x > result.x) result.x = s.x
					if (s.y > result.y) result.y = s.y
				}
			}
		}
		return result
	}

	/**
	 * @returns {Number} Returns the minimum of all visible non-empty series associated to axis, minimum x values.
	 */
	this.minXValue = function (axis) {
		var result = Infinity,
			v
		this.eachAxis(axis, s => {
			v = s.minXValue()
			if (v < result) result = v
		})
		return result
	}

	/**
	 * @returns {Number} Returns the minimum of all visible series mininum data values.
	 */
	this.minYValue = function (axis) {
		var result = Infinity,
			v
		this.eachAxis(axis, s => {
			v = s.minYValue()
			if (v < result) result = v
		})
		return result
	}

	/**
	 * @returns {Number} Returns the maximum of all visible series maximum x values.
	 */
	this.maxXValue = function (axis) {
		var result = -Infinity,
			v
		this.eachAxis(axis, s => {
			v = s.maxXValue()
			if (v > result) result = v
		})
		return result
	}

	/**
	 * @returns {Number} Returns the maximum of all visible series maximum data values.
	 */
	this.maxYValue = function (axis) {
		var result = -Infinity,
			v
		this.eachAxis(axis, s => {
			v = s.maxYValue()
			if (v > result) result = v
		})
		return result
	}

	function axisStrokeSize(axis) {
		if (axis.visible && axis.firstSeries) {
			var s = axis.format.stroke
			return s.fill === "" ? 0 : s.size * 0.5
		} else return 0
	}

	this.draw = function () {
		var len = this.items.length,
			ch = this.chart,
			c = ch.ctx,
			a = ch.aspect,
			t,
			s

		if (len > 0) {
			for (t = 0; t < len; t++) {
				s = this.items[t]
				if (s.visible && s.beforeDraw) s.beforeDraw()
			}

			var shouldClip = a.clip && this.anyUsesAxes(),
				axes = ch.axes

			if (shouldClip) {
				var chr = ch.chartRect,
					ax = axisStrokeSize(axes.left),
					ay = axisStrokeSize(axes.top),
					r = {
						x: chr.x + ax,
						y: chr.y + ay,
						width: chr.width - axisStrokeSize(axes.right),
						height: chr.height - axisStrokeSize(axes.bottom)
					}

				a.clipRect(r)
			}

			try {
				var groups = c.beginParent

				for (t = 0; t < len; t++) {
					s = this.items[t]

					if (s.visible) {
						var old = c.globalAlpha
						c.globalAlpha = 1 - s.format.transparency

						if (s.transform) {
							c.save()
							s.transform()
						}

						s.visual = groups ? c.beginParent() : null

						if (s.onbeforedraw) s.onbeforedraw(s)

						s.draw()

						if (groups) c.endParent()

						if (s.ondraw) s.ondraw(s)

						if (s.transform) c.restore()

						c.globalAlpha = old
					}
				}
			} finally {
				if (shouldClip)
					//a.clipRect(ch.bounds);
					c.restore()
			}

			for (t = 0; t < len; t++) {
				s = this.items[t]

				if (s.visible && s.marks.visible) {
					if (s.transform) {
						c.save()
						s.transform()
					}

					s.drawMarks()

					if (s.transform) c.restore()
				}
			}
		}
	}
}

/**
 * @type SeriesList
 */
/* Calls f function parameter for each series in list */
SeriesList.prototype.each = function (f) {
	var l = this.items.length,
		t = 0
	for (; t < l; t++) f(this.items[t])
}

SeriesList.prototype.eachAxis = function (axis, func) {
	var len = this.items.length,
		s
	while (len--) {
		s = this.items[len]
		if (s.visible && (!axis || s.associatedToAxis(axis)) && (s.__alwaysDraw || s.count() > 0)) func(s)
	}
}

function loadXML(xml)
{
  if (window.DOMParser)
    return new DOMParser().parseFromString(xml,"text/xml");
  else // Internet Explorer
  {
    xmlDoc=new ActiveXObject("Microsoft.XMLDOM");
    xmlDoc.async=false;
    xmlDoc.loadXML(xml);
    return xmlDoc;
  }
}

Series.prototype.loadXML=function(xml) {
  this.data.xml=xml;
  this.refresh();
}

function parseXML(series,xml,seriesTag,pointTag) {
  var data=series.data;
  data.values=[];
  data.labels=[];

  seriesTag=seriesTag || "series";

  var doc=loadXML(xml.value), n;

  var s = doc.getElementsByTagName(seriesTag)[0];
  if (s) {
    n= s.getAttribute("color");
    if (n) series.color=n;

    n= s.getAttribute("name");
    if (n) series.title=n;

    n= s.getAttribute("metric");
    if (n) data.title=n;

    pointTag=pointTag || "point";

    var points= s.getElementsByTagName(pointTag);
    if (points) {
      for (var t = 0; t < points.length; t++) {
        n = points[t].getAttribute("name");
        if (n) data.labels.push(n);

        n = points[t].getAttribute("value");
        data.values.push(parseFloat(n));

        n = points[t].getAttribute("x");
        if (n)
           data.x.push(parseFloat(n));
      }
    }
  }
}

Series.prototype.loadJSON=function(json) {
  this.data.json=json;
  this.refresh();
}

function parseJSON(series,json,seriesTag,pointTag) {
  var data=series.data, tmp;
  data.values=[];
  data.labels=[];

  seriesTag=seriesTag || "series";

  var o=(typeof json.value === "string" ) ? JSON.parse(json.value) : json.value;

  if (o.series) {
    if (o.series.name!="") series.title=o.series.name;
    if (o.series.color!="") series.format.fill=o.series.color;

    if (o.series.metric!="")
    {
      data.title=o.series.metric;

      tmp=series.mandatoryAxis.title;
      if (tmp.text=="") tmp.text=data.title;
    }

    if (o.series.category!="")
    {
      tmp=series.notmandatory.title;
      if (tmp.text=="") tmp.text=o.series.category;
    }

    var p=o.series.point, pp;

    if (p) {
      for(var t=0; t<p.length; t++) {

        pp=p[t];

        data.values.push(pp.value);

        if (pp.name !== undefined)
           data.labels[t]=pp.name;

        if (pp.x !== undefined) {
          if (!data.x) data.x=[];
          data.x[t]=pp.x;
        }

        if (pp.color && (pp.color!=='')) {
          if (!series.palette.colors) series.palette.colors=[];
          series.palette.colors[t]=pp.color;
        }
      }
    }
  }
}

/**
 * @constructor
 * @augments Tee.Series
 * @class Base abstract class to draw data as vertical or horizontal bars
 * @property {Number} [sideMargins=100] Defines the percent of bar size to use as spacing between axes.
 * @property {Boolean} [useOrigin=true] Determines if {Tee.CustomBar#origin} value is used as bar minimum.
 * @property {Number} [origin=0] Defines the value to use as bar minimum.
 * @property {Number} [barSize=70] Defines the percent size of bars on available space.
 * @property {Number} [customBarSize=0] Defines the custom bar size in pixels.
 * @property {Number} [offset=0] Defines the percent bar size to offset each bar.
 * @property {String} [barStyle="bar"] Which shape to draw ("bar", "ellipse", "line").
 * @property {Boolean} [stacked="no"] Use "no", "yes", "100", "sideAll", "self", "side" to define stack behaviour with other BarSeries.
 */
let CustomBar = function (o, o2) {
	Series.call(this, o, o2)

	this.sideMargins = 100
	this.useOrigin = true
	this.origin = 0
	this.continuous = false

	this.marks.visible = true
	this.marks.location = "end"

	this.hover.enabled = true

	this.offset = 0 // %
	this.barSize = 70 // %
	this.customBarSize = 0
	this.barStyle = "bar" // "ellipse"
	var percent = 1
	var f = this.format
	f.fill = ""
	f.stroke.fill = "black"
	f.shadow.visible = true
	f.round.x = 4
	f.round.y = 4
	f.gradient.visible = true

	f.depth = 1

	this.stacked = "no" // "yes", "100", "sideAll", "self", "side"

	this.drawBar = function (r, barStyle) {
		var old = f.depth,
			oldz = f.z,
			ctx = this.chart.ctx

		if (!barStyle) barStyle = this.barStyle

		f.depth = (0.5 * Math.min(this.yMandatory ? r.width : r.height, 200)) / 100 // 200=Three.totalDepth !!

		if (r.width > 0 && r.height > 0) {
			if (this.stacked !== "side") f.z = f.z + 0.5 * (1 - f.depth)

			if (barStyle === "bar") {
				f.cube(r)
			} else if (barStyle === "line") {
				var pos

				ctx.beginPath()
				ctx.z = f.z + f.depth * 0.5

				if (this.yMandatory) {
					pos = r.x + 0.5 * r.width

					ctx.moveTo(pos, r.y)
					ctx.lineTo(pos, r.y + r.height)

					//f.rectPath(pos,r.y,1,r.height);
				} else {
					pos = r.y + 0.5 * r.height

					ctx.moveTo(r.x, pos)
					ctx.lineTo(r.x + r.width, pos)

					//f.rectPath(r.x,pos,r.width,1);
				}
			} else if (barStyle === "cylinder") f.cylinder(r, 1, this.yMandatory)
			else if (barStyle === "cone") f.cylinder(r, 0, this.yMandatory)
			else if (barStyle === "ellipsoid" && this.chart.__webgl) {
				ctx.depth = f.depth
				ctx.z = f.z
				ctx.image = this.image
				ctx.ellipsoid(r, this.yMandatory)
			} else {
				f.z += 0.5 * f.depth
				f.ellipsePath(this.chart.ctx, r.x + r.width * 0.5, r.y + r.height * 0.5, r.width, r.height)
			}

			f.depth = old
			f.z = oldz

			return true
		} else return false
	}

	/**
	 * @returns {Number} Returns the number of visible Tee.CustomBar series that are displayed before this series.
	 */
	this.countAll = function (upToThis) {
		var i = this.chart.series.items,
			res = 0,
			len = i.length,
			t,
			s

		for (t = 0; t < len; t++) {
			s = i[t]

			if (s == this && upToThis) break
			else if (s.visible && s.constructor == this.constructor) res += s.data.values.length
		}

		return res
	}

	var offset = new Point(),
		originPos,
		bar = new Rectangle(),
		visibleBar = { total: 0, index: 0 }

	this._margin = 0

	this.calcBarOffset = function (axisSize) {
		var barSize = axisSize,
			all = this.stacked == "sideAll"

		this.countall = all ? this.countAll(true) : 0

		var tmpLen,
			len = all ? this.countAll() : this.data.values.length

		if (len > 1) barSize /= len

		if (this.stacked == "no") {
			barSize /= visibleBar.total
			offset.x =
				(this.customBarSize > 0 ? this.customBarSize : barSize * this.barSize * 0.01) *
				(visibleBar.total == 1 ? -0.5 : visibleBar.index - visibleBar.total * 0.5)
			tmpLen = visibleBar.total
		} else {
			offset.x = -barSize * 0.5
			tmpLen = 1
		}

		offset.y = this.customBarSize > 0 ? this.customBarSize : barSize * this.barSize * 0.01

		this._margin = 0.5 * tmpLen * offset.y + this.sideMargins * (tmpLen * (barSize - offset.y)) * 0.005

		if (this.stacked != "no") offset.x += this.offset * barSize * 0.01 + (barSize - offset.y) * 0.5
	}

	this.calcStackPos = function (t, p) {
		var v, tmp, a

		if (this.isStacked) {
			this.calcStack(t, p, this.data.values[t])
			;(v = this.pointOrigin(t, false)), (a = this.mandatoryAxis)

			if (this.isStack100) {
				tmp = this.pointOrigin(t, true)
				originPos = tmp === 0 ? a.endPos : a.calc((v * 100.0) / tmp)
			} else originPos = a.calc(v)
		} else {
			this.calc(t, p)

			if (this.stacked == "sideAll") {
				tmp = this.notmandatory.calc(this.countall + t)
				if (this.yMandatory) p.x = tmp
				else p.y = tmp
			}
		}
	}

	var hasPaintedOver = false

	this.drawSortedValues = function (sorted) {
		var animation = this.sortedOptions.sortedDrawAnimation
		if (this.sortedOptions.sorted != sorted && !this.sortedOptions.sorting && animation && !animation.running) {
			this.sortedOptions.sorted = sorted
			var data = this.data
			if (this.sortedOptions.sorted) this.sortValues()

			var prevValues = this.sortedOptions.sorted
				? this.sortedOptions.originalValues.slice()
				: this.sortedOptions.sortedValues.slice()
			var values = this.data.values
			var endValues = this.sortedOptions.sorted
				? this.sortedOptions.sortedValues.slice()
				: this.sortedOptions.originalValues.slice()

			var sortedOptions = this.sortedOptions
			animation.chart = this.chart
			if (sortedOptions.sortingAnimationType == "horizontalchange") {
				animation.doStep = f => {
					//do with draw
					if (f < 1) {
						percent = f
						this.draw()
					}
				}

				animation.onstop = () => {
					percent = 1
					if (sortedOptions.sorted)
						for (var i = 0; i < prevValues.length; i++) {
							values[i] = endValues[i]
						}
					//animation.running = false;
				}
				animation.onstart = () => {
					if (!sortedOptions.sorted)
						for (var i = 0; i < prevValues.length; i++) {
							values[i] = endValues[i]
						}
					//animation.running = true;
				}
			} else {
				animation.doStep = f => {
					//do with draw
					if (f < 1) {
						for (var i = 0; i < prevValues.length; i++) {
							values[i] = prevValues[i] + (endValues[i] - prevValues[i]) * f
						}
					}
				}

				animation.onstop = () => {
					percent = 1
					for (var i = 0; i < prevValues.length; i++) {
						values[i] = endValues[i]
					}
					animation.running = false
				}
				animation.onstart = () => {
					animation.running = true
				}
			}
			animation.animate()

			this.data.labels = this.sortedOptions.sorted
				? this.sortedOptions.sortedLabels.slice()
				: this.sortedOptions.originalLabels.slice()

			this.chart.draw()
		}
	}
	this.draw = function () {
		var len = this.data.values.length

		if (len > 0) {
			this.initOffsets()

			var p = new Point(),
				c = this.chart.ctx

			if (!this.hover.enabled) {
				f.stroke.prepare()
				f.shadow.prepare(c)
			}

			var hover = this.hover.enabled,
				isLine = this.barStyle === "line",
				_styles = this.data.styles

			if (hover && this.format.image.url != null) {
				this.hover.image.url = this.format.image.url
				this.hover.image.repeat = this.format.image.repeat
				this.hover.image.backFill = this.format.image.backFill
			}

			f.z = 0 //B407 init
			for (var t = 0; t < len; t++)
				if (!this.isNull(t)) {
					this.calcStackPos(t, p)

					var sortedP = new Point()

					if (percent < 1) {
						this.calcStackPos(
							this.sortedOptions.sortedValuesIndices.findIndex(x => x == t),
							sortedP
						)
						if (this.sortedOptions.sorted) p.x = (-p.x + sortedP.x) * percent + p.x
						else p.x = (p.x - sortedP.x) * percent + sortedP.x
					}

					if (this.onbeforedrawPoint) this.onbeforedrawPoint(t)

					this.calcBarBounds(p, bar, offset, originPos instanceof Array ? originPos[t] : originPos)

					this.barRectangle = bar
					var pointPainted = this.drawBar(bar, _styles ? _styles[t] : null)

					var isover = hover && this.over == t,
						ff = isover ? this.hover : f

					c.fillStyle = this.getFillStyle(bar, this.getFill(t, ff.fill === "" ? f : ff))

					if (!this.format.gradient.visible) ff.fill = c.fillStyle

					if (hover) ff.shadow.prepare(c)

					if (isover) {
						if (this.format.image.url != null && !hasPaintedOver) {
							//prevent fill flicker for first hover
							hasPaintedOver = true
							f.draw(c, null, bar)
						} else c.fill()
					}

					if (pointPainted) {
						ff.draw(c, null, bar)

						if (isLine || ff.stroke.fill !== "") {
							if (hover) ff.stroke.prepare()

							if (!isover && isLine) c.strokeStyle = c.fillStyle

							c.shadowColor = "transparent"

							c.stroke()

							if (ff.shadow.visible) c.shadowColor = ff.shadow.color
						}
					}
				}
		}
	}

	this.calcColorEach = function () {
		this.chart.series.visibleCount(this, Tee.CustomBar, visibleBar)
		this.isColorEach = this.colorEach == "yes" || (this.colorEach == "auto" && visibleBar.total == 1)
	}

	this.initOffsets = function () {
		var nomand = this.notmandatory,
			mand = this.mandatoryAxis,
			range = this.yMandatory ? this.maxXValue() - this.minXValue() : this.maxYValue() - this.minYValue()

		if (this.stacked == "sideAll") this.calcBarOffset(nomand.axisSize)
		else this.calcBarOffset(range === 0 ? nomand.axisSize : nomand.calcSize(range))

		if (this.useOrigin) {
			if (this.origin instanceof Array) {
				originPos = []
				for (var t = 0; t < this.origin.length; t++) {
					originPos[t] = mand.calc(this.origin[t])
				}
			} else {
				originPos = mand.calc(this.origin)
			}
		} else if (this.yMandatory) originPos = mand.inverted ? mand.startPos : mand.endPos
		else originPos = mand.inverted ? mand.endPos : mand.startPos

		var st = this.stacked

		this.isStacked = st !== "no" && st !== "sideAll" && st !== "side"
		this.isStack100 = st === "100"
	}

	/**
	 * @returns {Number} Returns the index of series bar that contains {@link Point} p parameter.
	 */
	this.clicked = function (p) {
		this.initOffsets()

		var p2 = new Point(),
			len = this.data.values.length,
			t

		for (t = 0; t < len; t++)
			if (!this.isNull(t)) {
				this.calcStackPos(t, p2)
				this.calcBarBounds(p2, bar, offset, originPos instanceof Array ? originPos[t] : originPos)

				if (bar.contains(p)) return t
			}

		return -1
	}

	this.markPos = function (t, p) {
		var yMand = this.yMandatory,
			op = offset.x + offset.y * 0.5,
			m = this.marks

		this.calcStackPos(t, p)

		if (this.stacked == "sideAll") {
			var tmp = this.notmandatory.calc(this.countall + t)
			yMand ? (p.x = tmp) : (p.y = tmp)
		}

		var inv = this.useOrigin && this.data.values[t] < (this.origin instanceof Array ? this.origin[t] : this.origin)
		if (this.mandatoryAxis.inverted) inv = !inv

		// Marks location

		if (m.location == "center") {
			this.calcBarBounds(p, bar, offset, originPos instanceof Array ? originPos[t] : originPos)

			if (m.canDraw(p.x, p.y, t, inv)) {
				if (yMand) p.y = bar.y + bar.height * 0.5 + m.bounds.height * 0.5
				else p.x = bar.x + bar.width * 0.5 - m.bounds.width * 0.5
			}
		}

		var a = this.chart.aspect,
			is3d = a.view3d,
			wx = 0,
			wy = 0

		if (is3d && a.orthogonal) {
			wx = a._orthox * 0.5
			wy = a._orthoy * 0.5
		}

		if (yMand) {
			p.x += is3d ? op + wx : op
			if (is3d) p.y -= wy
		} else {
			p.y += is3d ? op - wy : op
			if (is3d) p.x += wx
		}

		return inv
	}

	/*
this.drawMarks=function() {
	var len=this.data.values.length, m=this.marks, p=new Point(), inv;

	if (len>0)
		for(var t=0; t<len; t+=m.drawEvery)
			if (!this.isNull(t)) {
				inv=this.markPos(t,p);
				m.drawMark(p.x, p.y, t, inv);
			}
}
*/

	this.labelOrTitle = function (index) {
		var s = this.title,
			l = this.data.labels[index]
		return visibleBar.total > 1 ? s || l : this.parent.labelOrTitle(index)
	}

	this.initZ = function (index, total) {
		var s,
			f = this.format

		if (this.stacked !== "side") {
			f.z = 0
			f.depth = 1

			while (index > 1) {
				index--
				s = this.chart.series.items[index]

				if (s.visible && s.constructor == this.constructor) {
					f.z = s.z
					f.depth = s.depth
					break
				}
			}
		} else Tee.Series.prototype.initZ.call(this, index, total)

		this.marks.format.z = f.z + f.depth * 0.5
	}
}

CustomBar.prototype = new Series()
CustomBar.prototype.parent = Series.prototype
CustomBar.constructor = CustomBar

/**
 * @constructor
 * @augments CustomBar
 * @class Draws data as vertical bars
 */
let Bar = function (o, o2) {
	CustomBar.call(this, o, o2)

	this.calc = function (index, p) {
		this.isStacked ? this.calcStack(index, p, this.data.values[index]) : this.parent.calc.call(this, index, p)
	}

	this.horizMargins = function (p) {
		this.initOffsets()
		p.x = this._margin
		p.y = this._margin
	}

	this.vertMargins = function (p) {
		var m = this.marks,
			st = this.format.stroke,
			hasNeg = this.minYValue() < (this.origin instanceof Array ? ArrayMin(this.origin) : this.origin)

		if (m.visible && m.location !== "center") {
			p.y = m.arrow.length + m.format.textHeight("Wj") + m.margins.top + m.margins.bottom
			st = m.format.stroke
		}

		if (st.fill !== "") p.y += 2 * st.size + 1

		if (hasNeg) p.x = p.y
	}

	this.maxXValue = function () {
		return this.stacked === "sideAll" ? this.countAll() - 1 : this.parent.maxXValue.call(this)
	}

	this.minYValue = function () {
		var res = this.parent.minYValue.call(this)
		return this.useOrigin ? Math.min(this.origin instanceof Array ? ArrayMin(this.origin) : this.origin, res) : res
	}

	this.maxYValue = function () {
		if (this.stacked === "sideAll" || this.stacked === "side") {
			var res = 0,
				s,
				ss = this.chart.series.items,
				l = ss.length,
				t,
				val

			for (t = 0; t < l; t++) {
				s = ss[t]
				if (s.visible && s.constructor === this.constructor) {
					val = s.parent.maxYValue.call(s)
					if (val > res) res = val
				}
			}

			return res
		} else return this.stackMaxValue()
	}

	this.calcBarBounds = function (p, bar, offset, originPos) {
		bar.x = p.x + offset.x
		bar.width = offset.y

		if (this._vertAxis.inverted) {
			bar.y = originPos
			bar.height = p.y - bar.y
		} else {
			bar.y = p.y
			bar.height = originPos - p.y
		}

		if (bar.height < 0) {
			bar.y += bar.height
			bar.height = -bar.height
		}
	}
}

Bar.prototype = new CustomBar()
Bar.prototype.parent = CustomBar.prototype

/**
 * @constructor
 * @augments CustomBar
 * @class Draws data as horizontal bars
 */
let HorizBar = function (o, o2) {
	CustomBar.call(this, o, o2)

	this.yMandatory = false
	this.format.gradient.direction = "rightleft"

	/**
	 * @returns {Number} Returns the maximum width in pixels of series marks texts.
	 */
	this.maxMarkWidth = function () {
		var res = 0,
			f,
			t,
			m = this.marks,
			l = this.count(),
			n

		if (m.visible) {
			f = this.marks.format
			f.font.prepare()

			for (t = 0; t < l; t += m.drawEvery)
				if (!this.isNull(t)) {
					n = f.textWidth(this.markText(t) + "W")
					if (n > res) res = n
				}
		}
		return res
	}

	this.horizMargins = function (p) {
		var m = this.marks,
			st = this.format.stroke,
			hasNeg = this.minXValue() < this.origin

		if (m.visible && m.location !== "center") {
			p.y = m.arrow.length + this.maxMarkWidth() + m.margins.left + m.margins.right
			st = m.format.stroke
		}

		if (st.fill !== "") p.y += 2 * st.size + 1

		if (hasNeg) p.x = p.y
	}

	this.vertMargins = function (p) {
		this.initOffsets()
		p.x += this._margin
		p.y += this._margin
	}

	this.maxYValue = function () {
		return this.stacked == "sideAll" ? this.countAll() - 1 : this.parent.maxXValue.call(this)
	}

	this.minYValue = function () {
		return this.stacked == "sideAll" ? 0 : this.parent.minXValue.call(this)
	}

	this.minXValue = function () {
		var res = this.parent.minYValue.call(this)
		return this.useOrigin ? Math.min(this.origin, res) : res
	}

	this.maxXValue = function () {
		return this.stackMaxValue()
	}

	this.calcBarBounds = function (p, bar, offset, originPos) {
		bar.y = p.y + offset.x
		bar.height = offset.y

		if (this._horizAxis.inverted) {
			bar.x = p.x
			bar.width = originPos - p.x
		} else {
			bar.x = originPos
			bar.width = p.x - bar.x
		}

		if (bar.width < 0) {
			bar.x += bar.width
			bar.width = -bar.width
		}
	}
}

HorizBar.prototype = new CustomBar()
HorizBar.prototype.parent = CustomBar.prototype

/**
 * @constructor
 * @augments HorizBar
 * @class Draws a HorizBar with states in background and one limit.
 * @property {object}[limit] limit contains the properties of the limit.
 * @property {object}[states] states contains the properties of the states.
 */
let Bullet = function (o, o2) {
	HorizBar.call(this, [o[0]], o2 ? [o2[0]] : [])

	this.barSize = 25
	/**
	 * @property {number}[origin: 38] it contains the origin of the limit.
	 * @property {number}[width: 0.2] it contains the width of the limit.
	 * @property {number}[height: 35] it contains the height of the limit.
	 * @property {string}[color: "red"] it contains the color of the limit.
	 * @property {object}[bar: null] it contains the limit bar.
	 */
	this.limit = {
		origin: 38,
		width: 0.2,
		height: 35,
		color: "red",
		bar: null
	}
	this.marks.visible = false
	/**
	 * @property {array}[colors: ["#111", "#444", "#777", "#BBB", "#EEE"]] it contains the colors of the states.
	 * @property {array}[values: [10, 10, 10, 10, 10]] it contains the values of the states.
	 * @property {array}[barStates: []] it contains the bar array of the states.
	 * @property {number}[barSize: 50] it contains the size of the states.
	 * @property {boolean}[gradientVisible: false] it contains if the gradient will be visible or not.
	 */
	this.states = {
		colors: ["#111", "#444", "#777", "#BBB", "#EEE"],
		values: [10, 10, 10, 10, 10],
		barStates: [],
		barSize: 50,
		gradientVisible: false
	}
	createStatesBars(this.states, this.origin)
	function createStatesBars(states, origin) {
		var sumStatesValues = 0
		if (states.values.length > 0) {
			states.barStates.push(
				createBarState(
					states.values[0],
					sumStatesValues + origin,
					states.barSize,
					states.colors[0],
					states.colors[0],
					states.gradientVisible
				)
			)
			sumStatesValues += states.values[0]
			for (var i = 1; i < states.values.length; i++) {
				states.barStates.push(
					createBarState(
						states.values[i],
						sumStatesValues + origin,
						states.barSize,
						states.colors[(i - 1) % states.colors.length],
						states.colors[i % states.values.length],
						states.gradientVisible
					)
				)
				sumStatesValues += states.values[i]
			}
		}
	}

	this.minValue = function () {
		if (this.states.barStates.length >= 1) return this.states.barStates[0].origin
		else return 0
	}
	this.maxValue = function () {
		if (this.states.barStates.length >= 1)
			return this.states.barStates[this.states.barStates.length - 1].data.values[0]
		else return 0
	}
	function createBarState(value, origin, barSize, color1, color2, gradientVis) {
		var values = [value + origin]
		var bar = new Tee.HorizBar(values)
		bar.stacked = "side"
		bar.origin = origin
		bar.barSize = barSize
		bar.marks.visible = false
		bar.format.round.x = 0
		bar.format.round.y = 0
		bar.format.gradient.colors = [color1, color2]
		if (gradientVis) {
			bar.format.gradient.direction = "leftright"
			bar.format.gradient.visible = true
		} else {
			bar.format.gradient.visible = false
			bar.format.fill = color2
			bar.palette.colors = [color2]
		}
		bar.format.shadow.visible = false
		bar.format.stroke.fill = "rgba(0,0,0,0.0)"

		return bar
	}
	this.parentDraw = this.draw
	this.draw = function () {
		var minVal, maxVal
		minVal = this.minValue()
		maxVal = this.maxValue()
		this.chart.zoom.reset = function () {
			this.chart.axes.each(function () {
				this.automatic = true
			})
			this.chart.axes.bottom.setMinMax(minVal, maxVal)
		}
		this.states.barStates = []
		createStatesBars(this.states, this.origin)
		for (var i = 0; i < this.states.barStates.length; i++) {
			if (this.chart) this.states.barStates[i].setChart(this.states.barStates[i], this.chart)
			this.states.barStates[i].draw()
		}
		this.limit.bar = createBarState(
			this.limit.width,
			this.limit.origin + this.origin,
			this.limit.height,
			this.limit.color,
			this.limit.color,
			false
		)
		this.limit.bar.setChart(this.limit.bar, this.chart)
		this.limit.bar.draw()
		this.parentDraw()
	}
}
Bullet.prototype = new HorizBar()
Bullet.prototype.parent = HorizBar.prototype

/**
 * @constructor
 * @augments Tee.Series
 * @class Base abstract class for line, area, scatter plots
 * @property {Tee.CustomSeries-Pointer} pointer Paints a visual representation at each point position.
 * @property {String} stacked Defines if multiple series are displayed one on top of each other.
 * @property {Number} smooth Draws lines between points as diagonals (value 0) or smooth curves (value > 0 < 1).
 * @property {Boolean} [stairs=false] Draws lines between points in stairs mode instead of diagonals.
 * @property {Boolean} [invertedStairs=false] Draws lines between points in inverted stairs mode instead of diagonals.
 */
let CustomSeries = function (o, o2) {
	Series.call(this, o, o2)

	this.stacked = "no" // "yes", "100"
	this.stairs = false
	this.invertedStairs = false

	this.continuous = true //allows tooltip interpolation between points
	this.clickTolerance = 3 //distance for click sensitivity

	this.hover.enabled = true
	this.hover.line = false

	/*
		* @private
		*/
	this.isStacked = false
	this.isStack100 = false

	this.smooth = 0

	/**
	 * @constructor
	 * @public
	 * @class Formatting properties to draw symbols at series data positions
	 * @property {Number} [width=12] The horizontal size in pixels
	 * @property {Number} [height=12] The vertical size in pixels
	 * @property {Format} format Visual properties to paint pointers.
	 * @property {Boolean} [colorEach=false] Determines if pointers will be filled using a different color
	 * for each point in series.
	 * @property {String} [style="rectangle"] The shape to draw at pointer positions
	 */
	function Pointer(chart, series) {
		/*
			* @private
			*/
		this.setChart = function (chart) {
			this.chart = chart
			this.format.setChart(chart)
		}

		this.chart = chart

		this.inflateMargins = true

		/*
			* Visual properties to paint pointers
			*/
		var f = (this.format = new Format(chart))

		f.shadow.visible = false
		f.fill = ""
		f.gradient.colors = ["white", "white", "white"]
		f.gradient.visible = true
		f.shadow.visible = true

		/*
			* Determines if pointers will be displayed.
			*/
		this.visible = false

		this.colorEach = false

		/*
			* Visual style of pointer ("rectangle", "cylinder", "cone", "ellipse", "sphere", "triangle", "diamond", "downtriangle", "cross", "x").
			*/
		this.style = "rectangle"

		this.width = 12
		this.height = 12

		this.draw = function (p, index, f, fill) {
			var c = this.chart.ctx

			f.z = series.format.z

			if (this.transform) {
				c.save()
				this.transform(p.x, p.y, index)
			}

			var w = this.width * 0.5,
				h = this.height * 0.5,
				r

			if (this.style == "cube") {
				r = {
					x: p.x - w,
					y: p.y - h,
					width: this.width,
					height: this.height
				}

				var wh = Math.max(w, h) / 50 // 50=totalDepth !

				f.z = (series.format.z + series.format.depth) * 0.5 - wh * 0.5
				f.depth = wh

				f.cube(r)
				f.draw(c, null, r)
			} else if (this.style == "rectangle") f.rectangle(p.x - w, p.y - h, this.width, this.height)
			else if (this.style == "ellipse") f.ellipse(p.x, p.y, this.width, this.height)
			else if (this.style == "sphere") {
				f.depth = series.format.depth
				f.sphere(p.x, p.y, this.width, this.height)
			} else if (this.style == "cylinder") {
				r = {
					x: p.x - w,
					y: p.y - h,
					width: this.width,
					height: this.height
				}

				// Remember gradient properties:
				var g = f.gradient,
					oldDir = g.direction,
					oldColors = g.colors.slice(0)

				// Set gradient to resemble cylinder:
				g.direction = "leftright"
				g.colors = [g.colors[1], g.colors[0], g.colors[1]]

				f.cylinder(r, 1, true)
				f.draw(c, null, r)

				// Restore gradient properties:
				g.direction = oldDir
				g.colors = oldColors
			} else if (this.style == "cone") {
				r = {
					x: p.x - w,
					y: p.y - h,
					width: this.width,
					height: this.height
				}

				// Draw cylinder with top radius 0% to create a cone:
				f.cylinder(r, 0, true)

				f.draw(c, null, r)
			} else if (this.style == "triangle")
				f.polygon([new Point(p.x, p.y - h), new Point(p.x - w, p.y + h), new Point(p.x + w, p.y + h)])
			else if (this.style == "downtriangle")
				f.polygon([new Point(p.x, p.y + h), new Point(p.x - w, p.y - h), new Point(p.x + w, p.y - h)])
			else if (this.style == "diamond")
				f.polygon([
					new Point(p.x, p.y - h),
					new Point(p.x - w, p.y),
					new Point(p.x, p.y + h),
					new Point(p.x + w, p.y)
				])
			else {
				c.beginPath()

				if (this.style == "cross") {
					c.moveTo(p.x - w, p.y)
					c.lineTo(p.x + w, p.y)
					c.moveTo(p.x, p.y - h)
					c.lineTo(p.x, p.y + h)
				}
				if (this.style == "x") {
					c.moveTo(p.x - w, p.y - h)
					c.lineTo(p.x + w, p.y + h)
					c.moveTo(p.x - w, p.y + h)
					c.lineTo(p.x + w, p.y - h)
				}

				f.stroke.prepare(fill)
				c.stroke()
			}

			if (this.transform) c.restore()
		}

		this.setSize = function (size) {
			this.width = size
			this.height = size
		}
	}

	/*
		* Visual indication at series point positions.
		*/
	this.pointer = new Pointer(this.chart, this)

	this.maxYValue = function () {
		return this.stackMaxValue()
	}

	this.calc = function (index, p) {
		this.isStacked
			? this.calcStack(index, p, this.data.values[index])
			: Tee.Series.prototype.calc.call(this, index, p)
	}

	this.calcColorEach = function () {
		this.isColorEach =
			this.colorEach == "yes" || this.pointer.colorEach || this.colorEachLine == "yes" || this.colorEachLine == true
	}

	this.initZ = function (index, total) {
		var s,
			f = this.format

		if (this.stacked !== "no") {
			f.z = 0
			f.depth = 1

			while (index > 1) {
				index--
				s = this.chart.series.items[index]

				if (s.visible && s.constructor == this.constructor) {
					f.z = s.z
					f.depth = s.depth
					break
				}
			}
		} else Tee.Series.prototype.initZ.call(this, index, total)

		this.marks.z = f.z + f.depth * 0.5
	}
}

CustomSeries.prototype = new Series()

CustomSeries.prototype.drawPointers = function () {
	var len = this.data.values.length,
		f = this.pointer.format,
		isEach = this.colorEach == "yes" || this.pointer.colorEach

	if (!isEach) if (f.fill === "") f.fill = this.format.fill

	var p = new Point(),
		g = f.gradient,
		t,
		fill = f.fill,
		old = fill

	if (!isEach && g.visible) g.setEndColor(fill)

	for (t = 0; t < len; t++)
		if (!this.isNull(t)) {
			this.calc(t, p)

			fill = this.getFill(t, f)

			if (fill != old) {
				g.visible ? g.setEndColor(fill) : (f.fill = fill)
				old = fill
			}

			this.getSize(t)

			if (this.onbeforedrawpointer) this.onbeforedrawpointer(t, this.pointer)

			this.pointer.draw(p, t, f, fill)

			if (this.hover.enabled && this.over == t) this.pointer.draw(p, t, this.hover, fill)
		}

	if (isEach) f.fill = old
}

/**
 * @private
 */
CustomSeries.prototype.setChart = (series, chart) => {
	var tmp = Series.prototype.setChart
	tmp(series, chart)
	series.pointer.setChart(chart)
}

/**
 * @returns {Number} Returns the index of the series point that contains p {@link Point} parameter.
 */
CustomSeries.prototype.clicked = function (p) {
	var p1 = new Point(),
		p2 = new Point(),
		len = this.data.values.length,
		t

	// Line+Pointer only acts when this.hover.line=true

	if (this.drawLine && len > 0 && (this.hover.line || !this.pointer.visible)) {
		this.calc(0, p1)

		for (
			t = 1;
			t < len;
			t++ // if (!this.isNull(t))  <--- pending
		) {
			this.calc(t, p2)

			if (this.stairs) {
				var p1a

				if (this.invertedStairs) p1a = new Point(p2.x, p1.y)
				else p1a = new Point(p1.x, p2.y)

				if (pointInLine(p, p1, p1a, this.clickTolerance) || pointInLine(p, p1a, p2, this.clickTolerance)) return t
			} else if (pointInLine(p, p1, p2, this.clickTolerance))
				// near-line tolerance in pixels
				return t

			p1.x = p2.x
			p1.y = p2.y
		}
	}

	if (this.pointer.visible) {
		var r = new Rectangle(),
			po = this.pointer

		for (t = len - 1; t >= 0; t--)
			if (!this.isNull(t)) {
				this.calc(t, r)
				this.getSize(t)
				r.x -= po.width * 0.5
				r.width = po.width
				r.y -= po.height * 0.5
				r.height = po.height

				if (r.contains(p)) return t
			}
	}

	return -1
}

CustomSeries.prototype.horizMargins = function (p) {
	var po = this.pointer,
		s = po.format.stroke
	if (po.visible && po.inflateMargins) p.x = p.y = (s.fill !== "" ? s.size : 0) + 1 + po.width * 0.5
}

CustomSeries.prototype.vertMargins = function (p) {
	var po = this.pointer,
		s = po.format.stroke
	if (po.visible && po.inflateMargins) p.x = p.y = (s.fill !== "" ? s.size : 0) + 1 + po.height * 0.5
}

CustomSeries.prototype.getSize = () => {}

/**
 * @constructor
 * @augments CustomSeries
 * @class Draws series data as a contiguous polyline between points
 * @property {Boolean} [stairs=false] Determines if lines between points are direct (diagonals) or as stairs (horizontal and vertical).
 */
let Line = function (o, o2) {
	CustomSeries.call(this, o, o2)

	this.drawLine = true

	this.treatNulls = "dontPaint"

	this.colorEachLine = "no"

	var f = this.format
	f.shadow.visible = true
	f.shadow.blur = 10
	f.lineCap = "round"

	this.doDrawLine = function (c) {
		var p = new Point(),
			oldX,
			oldY,
			len = this.data.values.length,
			t,
			smop,
			s,
			begin = 0,
			end = len,
			no = this.notmandatory

		if (!this.smooth && !this.data.x) {
			begin = Math.max(0, trunc(no.minimum) - 1)
			end = Math.min(len, trunc(no.maximum) + 2)
		}

		var a = this.chart.aspect,
			is3d = a.view3d

		// ***** mods #2609 start here ******
		var isEach = this.colorEachLine == "yes" || this.colorEachLine == true

		f.fill = this.getFill(0, f)

		if (!isEach) if (f.fill === "") f.fill = this.format.fill

		c.beginPath() //clear c

		if (this.smooth > 0 && typeof Tee.drawSpline !== "undefined") {
			smop = new Array(2 * len)

			for (t = 0; t < len; t++) {
				this.calc(t, p)

				smop[2 * t] = p.x
				smop[2 * t + 1] = p.y
			}

			if (c.spline) c.spline(smop)
			else Tee.drawSpline(c, smop, this.smooth, true)
		} else {
			var noNulls = this.treatNulls !== "skip"

			var g = f.gradient
			var fill = f.stroke.fill
			var old = fill

			if (!isEach && g.visible) g.setEndColor(f.stroke.fill)

			var stSeg = f.stroke
			stSeg.prepare(stSeg.fill)

			for (t = begin; t < end; t++)
				if (this.isNull(t)) {
					if (noNulls) begin = -1 // Dont Paint until next non-null
				} else {
					this.calc(t, p)

					if (isEach) {
						if (isEach && t != begin) c.beginPath()

						fill = this.getFill(t, f)

						if (fill != old) {
							g.visible ? g.setEndColor(fill) : (f.fill = fill)
							old = fill
						}

						if (t == begin || begin === -1) {
							c.moveTo(p.x, p.y)
							begin = 0
						} else if (this.stairs) {
							if (this.invertedStairs) {
								c.moveTo(oldX, oldY)
								c.lineTo(p.x, oldY)
								c.lineTo(p.x, p.y)
							} else {
								c.moveTo(oldX, oldY)
								c.lineTo(oldX, p.y)
								c.lineTo(p.x, p.y)
							}
						} else {
							c.moveTo(oldX, oldY)
							c.lineTo(p.x, p.y)
						}

						c.strokeStyle = fill
						c.stroke()
					} else {
						if (t == begin || begin === -1) {
							c.moveTo(p.x, p.y)
							begin = 0
						} else if (this.stairs) {
							if (this.invertedStairs) c.lineTo(p.x, oldY)
							else c.lineTo(oldX, p.y)

							c.lineTo(p.x, p.y)
						} else c.lineTo(p.x, p.y)
					}

					oldX = p.x
					oldY = p.y

					if (isEach && t != begin) c.closePath()
				}
		}

		var st = f.stroke

		// Chrome bug with shadow and stroke size == 1
		if (this.chart.isChrome && f.shadow.visible) st.size = Math.max(1.1, st.size)

		c.z = f.z
		c.depth = f.depth

		s = st.fill
		if (s === "") s = f.fill

		st.prepare(s)
		f.shadow.prepare(c)

		if (is3d) {
			c.fillStyle = f.fill
			c.fill()
		}

		if (!isEach) if (s !== "") c.stroke()
	}

	this.draw = function () {
		var len = this.data.values.length

		if (len > 0) {
			this.isStacked = this.stacked != "no"
			this.isStack100 = this.stacked == "100"

			if (this.drawLine) this.doDrawLine(this.chart.ctx)

			if (this.pointer.visible) this.drawPointers()
		}
	}
}

Line.prototype = new CustomSeries()

/**
 * @constructor
 * @augments Tee.Line
 * @class Draws series data as points at vertical and horizontal axes positions
 */
let PointXY = function (o, o2) {
	Line.call(this, o, o2)
	this.hover.enabled = true
	this.pointer.visible = true
	this.drawLine = false
}

PointXY.prototype = new Line()

Series.prototype.cellRect = function (r, act, series) {
	var visible = { total: 0, index: -1 }
	r.setFrom(this.chart.chartRect)
	this.chart.series.visibleCount(this, series, visible)

	if (act && visible.total > 1) {
		var cols = Math.round(Math.sqrt(visible.total)),
			rows = Math.round(visible.total / cols)

		if (r.width > r.height) {
			var tmp = cols
			cols = rows
			rows = tmp
		}

		r.width /= cols
		r.x += 1.03 * (visible.index % cols) * r.width

		r.height /= rows
		r.y += 1.03 * trunc(visible.index / cols) * r.height

		// % spacing between multiple series
		r.width *= 0.94
		r.height *= 0.94
	}

	return r
}

/**
 * @constructor
 * @augments Series
 * @class Draws series data as slices of a circle
 * @property {Number} [rotation=0] Rotates all slices by specified degree from 0 to 360.
 * @property {Number[]} explode Determines percent of separation from each slice to pie center.
 * @property {Boolean} [clockwise = true] Direction of Values.
 */
let Pie = function (o, o2) {
	Series.call(this, o, o2)

	this.marks.style = "percent"

	this.donut = 0
	this.rotation = 0
	this.colorEach = "yes"
	this.useAxes = false
	this.continuous = false
	this.angleWidth = 360
	this.maxRadius = 100
	var f = this.format
	f.stroke.fill = "black"
	f.shadow.visible = true
	f.gradient.visible = true
	f.gradient.direction = "radial"
	f.gradient.colors = ["white", "white", "white"]

	this.hover.enabled = true

	this.sort = "values"
	this.orderAscending = false

	this.explode = null

	this.marks.visible = true

	this.concentric = false
	this.clockwise = true

	/**
	 * @returns {Number} Returns the index'th pie slice value.
	 */
	this.getValue = function (index) {
		return this.data.values[index]
	}

	this.calcCenter = function (t, radius, mid, center) {
		if (this.explode) {
			var v = this.explode[t]
			if (v) {
				v = radius * v * 0.01
				center.x += v * Math.cos(mid)
				center.y += v * Math.sin(mid)
			}
		}
	}

	this.clicked = function (p) {
		var c = this.chart.ctx,
			len = this.data.values.length,
			t,
			index

		//IE8 ExCanvas does not support "isPointInPath"

		if (c.isPointInPath) {
			endAngle = angle = (Math.PI * this.rotation) / 180.0

			for (t = 0; t < len; t++) {
				index = sorted ? sorted[t] : t

				if (!this.isNull(index)) {
					this.slice(c, index)

					if (c.isPointInPath(p.x, p.y)) return index
				}
			}
		}

		return -1
	}

	var total,
		piex,
		piey,
		radius,
		donutRadius,
		center = { x: 0, y: 0 },
		sorted,
		angle,
		endAngle,
		hoverang

	function calcPos(angle, p) {
		p.x = center.x + Math.cos(angle) * donutRadius
		p.y = center.y + Math.sin(angle) * donutRadius
	}

	// Return Pie radius, and returns pie center xy at "c" parameter
	this.getCenter = c => {
		c.x = center.x
		c.y = center.y
		return radius
	}

	this.slice = function (c, index) {
		var p = new Point()

		var a = (Math.PI * 2 * (Math.abs(this.data.values[index]) / total)) / (360 / this.angleWidth)
		endAngle += this.clockwise ? a : -a
		center.x = piex
		center.y = piey
		this.calcCenter(index, radius, (angle + endAngle) * 0.5, center)

		if (this.donut === 0) {
			p.x = center.x
			p.y = center.y
		} else calcPos(angle, p)

		var webgl = this.chart.__webgl

		if (webgl) {
			calcPos(2 * Math.PI - angle, p)
			c.slice(p, center, radius, angle, endAngle, donutRadius, f.tube, f.beveled)
		} else {
			c.beginPath()
			c.moveTo(p.x, p.y)
			c.arc(center.x, center.y, radius, angle, endAngle, !this.clockwise)

			if (this.donut !== 0) {
				calcPos(endAngle, p)
				c.lineTo(p.x, p.y)
				c.arc(center.x, center.y, donutRadius, endAngle, angle, this.clockwise)
			}

			c.closePath()
		}

		if (index == this.over) hoverang = angle

		angle = endAngle
	}

	this.fill = function (i) {
		return this.getFillStyle(
			new Rectangle(center.x - radius, center.y - radius, radius * 2, radius * 2),
			this.getFill(i)
		)
	}

	this.slices = function (shadow) {
		var c = this.chart.ctx,
			len = this.data.values.length,
			t,
			index

		endAngle = angle = (Math.PI * this.rotation) / 180.0

		// TODO: Replace with overriden Format.slice
		c.z = 0.5
		c.depth = 1

		for (t = 0; t < len; t++) {
			index = sorted ? sorted[t] : t

			if (this.onbeforedrawPoint) this.onbeforedrawPoint(index)

			if (!this.isNull(index)) {
				this.slice(c, index)

				if (shadow) f.shadow.prepare(c)
				else c.fillStyle = this.fill(index)

				c.fill()

				if (!shadow) {
					var st = f.stroke

					if (st.fill !== "") {
						st.prepare()
						c.stroke()
					}
				}
			}
		}
	}

	var r = new Rectangle()

	this.draw = function () {
		var len = this.data.values.length

		if (len > 0) {
			var h = 0,
				m = this.marks

			if (f.shadow.visible) h += 2 * f.shadow.height

			if (m.visible) {
				m.format.font.prepare()
				h += m.format.textHeight("Wj") + m.arrow.length * 0.5
			}

			this.cellRect(r, !this.concentric, Tee.Pie)

			piex = r.x + r.width * 0.5
			piey = r.y + r.height * 0.5

			radius = r.width * 0.5
			var r2 = (r.height - 2 * h) * 0.5

			if (r2 < 0) r2 = 0
			if (r2 < radius) radius = r2

			donutRadius = radius * this.donut * 0.01
			radius = radius / (100 / this.maxRadius)
			total = ArraySumAbs(this.data.values)

			sorted = this.doSort(this.sort, this.orderAscending)

			if (!this.chart.__webgl) this.slices(true)

			this.slices(false)

			if (this.hover.enabled && this.over != -1) {
				var st = this.hover
				if (st.stroke.fill !== "") {
					endAngle = angle = hoverang

					var c = this.chart.ctx
					this.slice(c, this.over)
					c.fillStyle = this.fill(this.over)
					st.draw(c, null, r)
				}
			}
		}
	}

	this.drawMarks = function () {
		var endAngle = (Math.PI * this.rotation) / 180.0,
			angle = endAngle,
			mid,
			v = this.data.values,
			len = v.length,
			index,
			t,
			a

		this.marks.format.z = 0.5

		for (t = 0; t < len; t += this.marks.drawEvery) {
			index = sorted ? sorted[t] : t

			if (!this.isNull(index)) {
				a = Math.PI * 2 * (Math.abs(v[index]) / total)
				endAngle += this.clockwise ? a : -a
				mid = (angle + endAngle) * 0.5
				center.x = piex
				center.y = piey
				this.calcCenter(t, radius, mid, center)
				this.marks.drawPolar(center, radius, mid, index)
				angle = endAngle
			}
		}
	}
}

Pie.prototype = new Series()

/**
 * @constructor
 * @augments Tee.CustomSeries
 * @class Draws series data as filled mountain segments between points
 * @property {Boolean} [useOrigin=false] Determines if {Tee.Area#origin} value is used as area minimum.
 * @property {Number} [origin=0] Defines the value to use as area minimum.
 */
let Area = function (o, o2) {
	CustomSeries.call(this, o, o2)

	this.useOrigin = false
	this.origin = 0
	this.drawLine = true
	this.closeArea = true

	var f = this.format
	f.shadow.visible = true
	f.lineCap = "round"
	f.stroke.fill = "black"
	f.fill = ""
	f.beveled = true

	f.depth = 1
	f.z = 0.5

	var r = new Rectangle()

	this.draw = function () {
		var len = this.data.values.length

		if (len > 0) {
			var a = this.mandatoryAxis,
				nm = this.notmandatory,
				originPos,
				isY = this.yMandatory

			if (this.useOrigin) originPos = a.calc(this.origin)
			else if ((isY && a.inverted) || (!isY && !a.inverted)) originPos = a.startPos
			else originPos = a.endPos

			this.isStacked = this.stacked != "no"
			this.isStack100 = this.stacked == "100"

			var start,
				p = new Point(),
				old,
				t,
				c = this.chart.ctx,
				smop,
				doStack = this.isStacked, // && (visibleBar.index>0),
				begin = 0,
				end = len

			if (!this.smooth && !this.data.x) {
				begin = Math.max(0, trunc(nm.minimum) - 1)
				end = Math.min(len, trunc(nm.maximum) + 2)
			}

			c.depth = f.depth
			c.z = f.z

			var closePoint

			c.beginPath()

			if (this.smooth > 0 && typeof Tee.drawSpline !== "undefined") {
				smop = new Array(2 * len)

				for (t = 0; t < len; t++) {
					this.calc(t, p)

					smop[2 * t] = p.x
					smop[2 * t + 1] = p.y
				}

				start = isY ? smop[0] : smop[1]

				if (c.spline) c.spline(smop, true)
				else Tee.drawSpline(c, smop, this.smooth, true)

				if (doStack) {
					var tmp = 0

					for (t = len - 1; t >= 0; t--) {
						this.calcStack(t, p, 0)
						smop[tmp++] = p.x
						smop[tmp++] = p.y
					}

					c.lineTo(smop[0], smop[1])

					if (c.spline) c.spline(smop, true)
					else Tee.drawSpline(c, smop, this.smooth, false)
				}
			}
			//  if (!this.isNull(t)) <-- pending
			else {
				this.calc(begin, p)
				c.moveTo(p.x, p.y)
				start = isY ? p.x : p.y
				old = isY ? p.y : p.x

				if (this.stairs)
					for (t = begin + 1; t < end; t++) {
						this.calc(t, p)
						c.lineTo(p.x, old)
						c.lineTo(p.x, p.y)
						old = isY ? p.y : p.x
					}
				else
					for (t = begin + 1; t < end; t++) {
						this.calc(t, p)
						c.lineTo(p.x, p.y)
					}
			}

			if (doStack) {
				if (this.smooth === 0)
					for (t = end - 1; t >= begin; t--) {
						this.calcStack(t, p, 0)
						if (this.stairs) {
							c.lineTo(p.x, old)
							c.lineTo(p.x, p.y)
							old = isY ? p.y : p.x
						} else c.lineTo(p.x, p.y)
					}
			} else {
				if (isY) {
					if (this.closeArea) {
						c.lineTo(p.x, originPos)
						c.lineTo(start, originPos)
					} else closePoint = p
				} else {
					c.lineTo(originPos, p.y)
					c.lineTo(originPos, start)
				}
			}

			if (!this.closeArea) {
				var st = f.stroke
				var s = st.fill
				st.prepare(s)
				c.stroke()
				c.lineTo(closePoint.x, originPos)
				c.lineTo(start, originPos)
				var tmpStrokeFill = f.stroke.fill
				f.stroke.fill = "#00FF0000"
			}

			c.closePath()

			var g = f.gradient
			if (g.visible) g.colors[g.colors.length - 1] = f.fill

			this.bounds(r)

			if (c.__webgl) c.beveled = f.beveled

			f.draw(c, null, r)

			if (!this.closeArea) f.stroke.fill = tmpStrokeFill

			if (this.pointer.visible) this.drawPointers()
		}
	}

	this.minYValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.minYValue.call(this) : Tee.Series.prototype.minXValue.call(this)
		return this.yMandatory ? (this.useOrigin ? Math.min(v, this.origin) : v) : v
	}

	this.minXValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.minXValue.call(this) : Tee.Series.prototype.minYValue.call(this)
		return this.yMandatory ? v : this.useOrigin ? Math.min(v, this.origin) : v
	}

	this.maxYValue = function () {
		var v = this.yMandatory ? this.stackMaxValue() : Tee.Series.prototype.maxXValue.call(this)
		return this.yMandatory ? (this.useOrigin ? Math.max(v, this.origin) : v) : v
	}

	this.maxXValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.maxXValue.call(this) : this.stackMaxValue()
		return this.yMandatory ? v : this.useOrigin ? Math.max(v, this.origin) : v
	}

	this.vertMargins = function (p) {
		if (this.yMandatory && f.stroke.fill !== "") p.y += f.stroke.size + 2
	}

	this.horizMargins = function (p) {
		if (!this.yMandatory && f.stroke.fill !== "") p.y += f.stroke.size + 2
	}
}

Area.prototype = new CustomSeries()

let HighLowBar = function (o, o2) {
	CustomSeries.call(this, o, o2)

	this.useOrigin = false
	this.origin = 0
	this.drawLine = true
	this.closeArea = true

	var f = this.format
	f.shadow.visible = true
	f.lineCap = "round"
	f.stroke.fill = "black"
	f.fill = ""
	f.beveled = true

	this.maxmin = new Format(f.chart)
	var b = this.maxmin
	b.shadow.visible = false
	b.lineCap = "round"
	b.stroke.fill = "red"
	b.fill = ""
	b.beveled = true

	f.depth = 1
	f.z = 0.5

	this.data.lows = []

	this.addRandom = function (count) {
		var d = this.data

		if (!count) count = 5

		d.values.length = count

		d.x = null
		d.lows = []
		d.lows.length = count

		if (count > 0) {
			for (var t = 0; t < count; t++) {
				d.values[t] = Math.random() * 1000
				d.lows[t] = 50 + Math.random() * 150
			}
		}
	}

	this.calcLows = function (index, p) {
		this.isStacked ? this.calcStack(index, p, this.data.lows[index]) : Tee.Series.prototype.calc.call(this, index, p)
	}

	var r = new Rectangle()

	this.draw = function () {
		var len = this.data.values.length

		if (len > 0) {
			var a = this.mandatoryAxis,
				nm = this.notmandatory,
				originPos,
				isY = this.yMandatory

			if (this.useOrigin) originPos = a.calc(this.origin)
			else if ((isY && a.inverted) || (!isY && !a.inverted)) originPos = a.startPos
			else originPos = a.endPos

			this.isStacked = this.stacked != "no"
			this.isStack100 = this.stacked == "100"

			var start,
				p = new Point(),
				old,
				t,
				c = this.chart.ctx,
				smop,
				doStack = this.isStacked, // && (visibleBar.index>0),
				begin = 0,
				end = len

			if (!this.smooth && !this.data.x) {
				begin = Math.max(0, trunc(nm.minimum) - 1)
				end = Math.min(len, trunc(nm.maximum) + 2)
			}

			c.depth = f.depth
			c.z = f.z

			var closePoint
			var maxP = []
			var minP = []
			var idx = 0

			c.beginPath()

			if (this.smooth > 0 && typeof Tee.drawSpline !== "undefined") {
				smop = new Array(2 * len)

				for (t = 0; t < len; t++) {
					this.calc(t, p)

					smop[2 * t] = p.x
					smop[2 * t + 1] = p.y
				}

				start = isY ? smop[0] : smop[1]

				if (c.spline) c.spline(smop, true)
				else Tee.drawSpline(c, smop, this.smooth, true)

				if (doStack) {
					var tmp = 0

					for (t = len - 1; t >= 0; t--) {
						this.calcStack(t, p, 0)
						smop[tmp++] = p.x
						smop[tmp++] = p.y
					}

					c.lineTo(smop[0], smop[1])

					if (c.spline) c.spline(smop, true)
					else Tee.drawSpline(c, smop, this.smooth, false)
				}
			}
			//  if (!this.isNull(t)) <-- pending
			else {
				this.calc(begin, p)
				c.moveTo(p.x, p.y)
				start = isY ? p.x : p.y
				old = isY ? p.y : p.x

				if (this.stairs)
					for (t = begin + 1; t < end; t++) {
						this.calc(t, p)
						c.lineTo(p.x, old)
						c.lineTo(p.x, p.y)
						old = isY ? p.y : p.x
					}
				else if (this.rects)
					for (t = begin; t < end; t++) {
						//var startPx = p.x;
						var lowP = p
						this.calc(t, p)
						var lowPY = this.mandatoryAxis.calc(this.data.lows[t])
						var nextPx = this.notmandatory.calc(this.data.x[t + 1])
						//this.calcLows(t, lowP);
						c.rect(p.x, p.y, nextPx - p.x, lowPY - p.y)
						//c.lineTo(p.x, old);
						//c.lineTo(p.x, p.y);
						old = isY ? p.y : p.x

						maxP[idx] = new Point(p.x, p.y)
						minP[idx] = new Point(p.x, lowPY)
						idx++
						maxP[idx] = new Point(nextPx, p.y)
						minP[idx] = new Point(nextPx, lowPY)
						idx++
					}
				else
					for (t = begin + 1; t < end; t++) {
						this.calc(t, p)
						c.lineTo(p.x, p.y)
					}
			}

			if (doStack) {
				if (this.smooth === 0)
					for (t = end - 1; t >= begin; t--) {
						this.calcStack(t, p, 0)
						if (this.stairs) {
							c.lineTo(p.x, old)
							c.lineTo(p.x, p.y)
							old = isY ? p.y : p.x
						} else c.lineTo(p.x, p.y)
					}
			} else {
				if (isY) {
					if (this.closeArea) {
						c.lineTo(p.x, originPos)
						c.lineTo(start, originPos)
					} else closePoint = p
				} else {
					c.lineTo(originPos, p.y)
					c.lineTo(originPos, start)
				}
			}

			//c.polygon(maxP);

			/*if (!this.closeArea) {
				var st = f.stroke;
				var s = st.fill;
				st.prepare(s);
				c.stroke();
				c.lineTo(closePoint.x, originPos);
				c.lineTo(start, originPos);
			}*/

			c.closePath()

			var g = f.gradient
			if (g.visible) g.colors[g.colors.length - 1] = f.fill

			this.bounds(r)

			if (c.__webgl) c.beveled = f.beveled

			f.draw(c, null, r)

			c.beginPath()
			for (t = 0; t < maxP.length; t++) {
				c.lineTo(maxP[t].x, maxP[t].y)
			}
			//if (maxP.length>0)
			//  c.moveTo(maxP[maxP.length-1].x+1, maxP[maxP.length-1].y+1);
			//c.closePath();
			f.stroke.prepare(f.fill, c)
			c.stroke()

			var oldStroke = f.stroke.fill
			var oldFill = f.fill
			var oldSize = f.stroke.size
			f.stroke.fill = "white"
			f.stroke.size = 0.75
			f.fill = "rgba(123,0,0,0.0)"
			f.draw(c, null, r)
			f.stroke.fill = oldStroke
			f.fill = oldFill
			f.stroke.size = oldSize

			c.beginPath()
			for (t = 0; t < minP.length; t++) {
				c.lineTo(minP[t].x, minP[t].y)
			}
			//if (minP.length>0)
			//c.moveTo(minP[minP.length-1].x+1, minP[minP.length-1].y+1);

			//c.closePath();

			var oldStroke = f.stroke.fill
			var oldFill = f.fill
			var oldSize = f.stroke.size
			f.stroke.fill = "white"
			f.stroke.size = 0.75
			f.fill = "rgba(123,0,0,0.0)"
			f.draw(c, null, r)
			f.stroke.fill = oldStroke
			f.fill = oldFill
			f.stroke.size = oldSize

			f.stroke.prepare(f.fill, c)
			c.stroke()

			//this.format.stroke.prepare(this.format.stroke.fill, c);
			//c.stroke();

			if (this.pointer.visible) this.drawPointers()
		}
	}

	this.minYValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.minYValue.call(this) : Tee.Series.prototype.minXValue.call(this)
		return this.yMandatory ? (this.useOrigin ? Math.min(v, this.origin) : v) : v
	}

	this.minXValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.minXValue.call(this) : Tee.Series.prototype.minYValue.call(this)
		return this.yMandatory ? v : this.useOrigin ? Math.min(v, this.origin) : v
	}

	this.maxYValue = function () {
		var v = this.yMandatory ? this.stackMaxValue() : Tee.Series.prototype.maxXValue.call(this)
		return this.yMandatory ? (this.useOrigin ? Math.max(v, this.origin) : v) : v
	}

	this.maxXValue = function () {
		var v = this.yMandatory ? Tee.Series.prototype.maxXValue.call(this) : this.stackMaxValue()
		return this.yMandatory ? v : this.useOrigin ? Math.max(v, this.origin) : v
	}

	this.vertMargins = function (p) {
		if (this.yMandatory && f.stroke.fill !== "") p.y += f.stroke.size + 2
	}

	this.horizMargins = function (p) {
		if (!this.yMandatory && f.stroke.fill !== "") p.y += f.stroke.size + 2
	}
}

HighLowBar.prototype = new CustomSeries()

/**
 * @constructor
 * @augments Area
 * @class Horizontal area style
 */
let HorizArea = function (o, o2) {
	Area.call(this, o, o2)
	this.yMandatory = false
}
HorizArea.prototype = new Area()

/**
 * @constructor
 * @augments Pie
 * @class Draws series data as slices of a circle, with a center hole
 * @property {Number} [donut=50] Percent of hole size relative to pie radius. From 0 to 100.
 */
let Donut = function (o, o2) {
	var lessDonutWidth = 100
	var donutArray = []
	Pie.call(this, o, o2)
	this.donut = 50

	this.refreshWidth = function () {
		if (this.concentric) {
			donutArray = this.chart.series.items
			var nVisibleDonuts = 0
			var n = 0
			for (var i = 0; i < donutArray.length; i++) {
				if (donutArray[i].visible) nVisibleDonuts++
			}
			if (lessDonutWidth == 100) getLessDonutWidth()
			for (var i = 0; i < donutArray.length; i++) {
				if (donutArray[i].visible) {
					donutArray[i].donut = lessDonutWidth + n * ((100 - lessDonutWidth) / nVisibleDonuts)
					n++
				}
			}
		}
	}
	function getLessDonutWidth() {
		for (var i = 0; i < donutArray.length; i++) {
			if (donutArray[i].donut < lessDonutWidth) lessDonutWidth = donutArray[i].donut
		}
	}
}

Donut.prototype = new Pie()

/**
 * @constructor
 * @augments Donut
 * @class Draws comparative of values plotted as concentric circular bands
 * @property {Number} [rotation=270] Rotates all slices by specified degree from 0 to 360.
 * @property {Number} [angleWidth=values%] Indicates the width of the Donut/Pie/ActivityGauge in degrees form 0 to 360.
 */

let ActivityGauge = function (o, o2) {
	Donut.call(this, [], [])
	this.data = {
		values: o != null ? o : [],
		labels: o2 != null ? o2 : []
	}

	this.donutArray = []
	this.maxWidth = 230
	this.maxDrawWidth = this.maxWidth
	this.addRandom = function (count) {
		for (var i = 0; i < count; i++) {
			this.add(Math.floor(Math.random() * 20 + 10), String.fromCharCode(65 + i))
		}
		return this
	}
	this.maxValue = function () {
		var tmp = this.data.values[0]
		for (var i = 0; i < this.data.values.length; i++) {
			if (tmp < this.data.values[i]) tmp = this.data.values[i]
		}
		return tmp
	}

	if (o != null) {
		for (var i = 0; i < o.length; i++) {
			var donutCenterSize = 40
			var tmpDonut = donutCenterSize + ((100 - donutCenterSize) * i) / o.length
			var tmpMaxRadius = donutCenterSize + ((100 - donutCenterSize) * (i + 1)) / o.length
			var tmpAngleWidth = Math.abs((this.maxDrawWidth * o[o.length - 1 - i]) / this.maxValue())
			this.donutArray.push(
				createDonut(o[o.length - 1 - i], o2[o.length - 1 - i], tmpDonut, tmpMaxRadius, tmpAngleWidth)
			)
		}
	}

	this.clicked = function (p) {
		var index = -1,
			i = 0
		while (i < this.donutArray.length && index == -1) {
			if (index == -1 && this.donutArray[i].clicked(p) != -1) index = this.donutArray.length - i - 1
			i++
		}
		return index
	}
	function createDonut(value, label, tmpDonut, maxRadius, angleWidth) {
		var donut = new Tee.Donut([value], [label])
		donut.concentric = true
		donut.marks.visible = false
		donut.format.shadow.visible = false
		donut.format.gradient.visible = false
		donut.donut = tmpDonut
		donut.maxRadius = maxRadius
		donut.angleWidth = angleWidth
		donut.rotation = 270
		donut.visible = false
		return donut
	}

	function copyFormat(donut, origin, colorNum) {
		var dF = donut.format
		var f = origin.format
		dF.fill =
			origin.chart.palette.colors[origin.donutArray.length - 1 - (colorNum % origin.chart.palette.colors.length)]
		dF.font.baseLine = f.font.baseLine
		dF.font.fill = f.font.fill
		dF.font.style = f.font.style
		dF.font.textAlign = f.font.textAlign
		dF.gradient.colors = [f.gradient.colors[0][colorNum]]
		dF.gradient.direction = f.gradient.direction
		dF.gradient.offset.x = f.gradient.offset.x
		dF.gradient.offset.y = f.gradient.offset.y
		dF.gradient.stops = f.gradient.stops
		dF.gradient.visible = f.gradient.visible
		dF.round.x = f.round.x
		dF.round.y = f.round.y
		dF.shadow.blur = f.shadow.blur
		dF.shadow.color = f.shadow.color
		dF.shadow.height = f.shadow.height
		dF.shadow.visible = f.shadow.visible
		dF.shadow.width = f.shadow.width
		dF.stroke.fill = f.stroke.fill.slice(0)
		dF.stroke.cap = f.stroke.cap
		dF.stroke.dash = f.stroke.dash
		dF.stroke.join = f.stroke.join
		dF.stroke.size = f.stroke.size
		dF.transparency = f.transparency
		donut.fill = i => (dF.gradient.visible ? dF.gradient.colors : dF.fill)
	}
	this.recalcWidth = function () {
		for (var i = 0; i < this.donutArray.length; i++) {
			var donutCenterSize = 40
			this.donutArray[i].donut = donutCenterSize + ((100 - donutCenterSize) * i) / this.data.values.length
			this.donutArray[i].maxRadius = donutCenterSize + ((100 - donutCenterSize) * (i + 1)) / this.data.values.length
			this.donutArray[i].angleWidth = Math.abs(
				(this.maxDrawWidth * this.data.values[this.data.values.length - 1 - i]) / this.maxValue()
			)
		}
	}

	this.minValue = function () {
		var tmp = this.data.values[0]
		for (var i = 0; i < this.data.values.length; i++) {
			if (tmp > this.data.values[i]) tmp = this.data.values[i]
		}
		return tmp
	}
	this.add = function (value, label) {
		var donutTmp = createDonut(value, label, 0, 0, 0)
		this.donutArray.push(donutTmp)
		this.data.values.push(value)
		this.data.labels.push(label)
		if (this.chart != null) this.linkDonutsToChart()
	}
	this.draw = function () {
		for (var i = 0; i < this.donutArray.length; i++) {
			var c = this.donutArray[i].chart.ctx
			copyFormat(this.donutArray[i], this, i)
			c.fillStyle = this.donutArray[i].getFillStyle(
				this.donutArray[i].chart.chartRect,
				this.donutArray[i].format.fill
			)
			this.recalcWidth()
			this.donutArray[i].draw()
		}
	}
	this.linkDonutsToChart = function () {
		for (var i = 0; i < this.donutArray.length; i++) {
			this.donutArray[i].setChart(this.donutArray[i], this.chart)
		}
	}
}
ActivityGauge.prototype = new Donut()

/**
 * @constructor
 * @augments Tee.Series
 * @class Draws series data as Gantt horizontal bars with start and end datetime values.
 * @property {Number} [height=70] Percent of gantt bar height. From 0 to 100.
 */
let Gantt = function (o, o2) {
	Series.call(this, o, o2)

	this.yMandatory = false

	this.dateFormat = "mediumDate"

	this.hover.enabled = true
	this.hover.round.x = this.hover.round.y = 8

	this.nextTasks = []
	this.nextTasksStrokeStyle = "Black"
	this.nextTasksPosition = "back" //back, front
	this.colorEach = "yes"

	this.data.start = this.data.values
	this.data.x = []
	this.data.end = []

	this.height = 70
	this.margin = new Point(6, 6)
	this.continuous = false
	var f = this.format
	f.shadow.visible = true
	f.round.x = f.round.y = 8
	f.stroke.fill = "black"
	f.gradient.visible = true

	var r = new Rectangle(),
		_h

	this.addNextTask = function (point1, point2) {
		this.nextTasks.push([point1, point2])
	}

	this.addRandom = function (count) {
		if (!count) count = 5

		var d = this.data

		d.x.length = count
		d.start.length = count
		d.end.length = count

		if (count > 0) {
			var year = 2012,
				month,
				day

			for (var t = 0; t < count; t++) {
				d.x[t] = t

				month = trunc(Math.random() * 12)
				day = trunc(Math.random() * 10)
				d.start[t] = new Date(year, month, day)
				if (month < 5) month = 5 + trunc(Math.random() * 7)
				d.end[t] = new Date(year, month, day + Math.random() * 10)
			}
		}
	}

	this.bounds = function (index, r) {
		if (this.isNull(index)) return false
		else {
			this.calc(index, r)
			r.y -= _h * 0.5
			r.width = this.data.end ? this.mandatoryAxis.calcSize(this.data.end[index] - this.data.start[index]) : 0
			r.height = _h

			return true
		}
	}

	this.add = function (pos, label, start, end) {
		var d = this.data
		d.labels.push(label)
		d.x.push(pos)
		d.start.push(start)
		d.end.push(end)
	}

	this.clicked = function (p) {
		var len = this.data.values.length,
			t

		for (t = 0; t < len; t++) if (this.bounds(t, r) && r.contains(p)) return t

		return -1
	}

	this.draw = function () {
		var len = this.data.values.length,
			t,
			ff,
			hover = this.hover,
			oldFill = hover.fill,
			c = this.chart.ctx,
			punts = []
		if (this.nextTasksPosition == "back") {
			drawLines(this)
		}
		_h = this.notmandatory.calcSize(this.height * 0.01)

		for (t = 0; t < len; t++)
			if (this.bounds(t, r)) {
				ff = hover.enabled && this.over === t ? hover : f
				ff.fill = this.getFillStyle(r, this.getFill(t, ff))
				ff.rectangle(r)
			}

		hover.fill = oldFill
		if (this.nextTasksPosition == "front") {
			drawLines(this)
		}
		function drawLines(gantt) {
			for (var i = 0; i < gantt.nextTasks.length; i++) {
				c.beginPath()
				c.strokeStyle = gantt.nextTasksStrokeStyle
				c.lineWidth = 2
				c.fillStyle = "000000"
				punts.push(Math.round(gantt.chart.axes.bottom.calc(gantt.data.end[gantt.nextTasks[i][0]])))
				punts.push(Math.round(gantt.chart.axes.left.calc(gantt.data.x[gantt.nextTasks[i][0]])))
				punts.push(Math.round(gantt.chart.axes.bottom.calc(gantt.data.start[gantt.nextTasks[i][1]])))
				punts.push(Math.round(gantt.chart.axes.left.calc(gantt.data.x[gantt.nextTasks[i][1]])))

				c.moveTo(punts[0], punts[1])
				c.lineTo(punts[0] - (punts[0] - punts[2]) / 2, punts[1])
				c.lineTo(punts[0] - (punts[0] - punts[2]) / 2, punts[3])
				c.lineTo(punts[2], punts[3])

				c.stroke()

				punts = []
			}
		}
	}

	this.horizMargins = function (p) {
		p.x = this.margin.x
		p.y = this.margin.y
	}

	this.minYValue = function () {
		return this.parent.minXValue.call(this) - 0.5
	}

	this.maxYValue = function () {
		return this.parent.maxXValue.call(this) + 0.5
	}

	this.minXValue = function () {
		return ArrayMin(this.data.start)
	}

	this.maxXValue = function () {
		return ArrayMax(this.data.end)
	}
}

Gantt.prototype = new Series()
Gantt.prototype.parent = Series.prototype

/**
 * @constructor
 * @augments PointXY
 * @class Draws data as points, each one with a different size or radius
 * @property {Object} data Contains each bubble x, value and radius.
 * @property {Number[]} data.radius Defines each bubble radius value.
 */
let Bubble = function (o, o2) {
	PointXY.call(this, o, o2)

	var p = this.pointer
	p.colorEach = true
	p.style = "sphere"
	p.format.gradient.visible = true
	p.format.gradient.direction = "radial"

	/**
	 * When true, horizontal and vertical edge margins are calculated.
	 */
	this.inflate = true

	this.data.radius = []

	this.addRandom = function (count) {
		var d = this.data

		if (!count) count = 5

		d.values.length = count

		d.x = null
		d.radius = []
		d.radius.length = count

		if (count > 0) {
			for (var t = 0; t < count; t++) {
				d.values[t] = Math.random() * 1000
				d.radius[t] = 50 + Math.random() * 150
			}
		}
	}
}

Bubble.prototype.initZ = function () {
	this.parent.prototype.initZ.call(this)
	this.format.marks.z = this.format.z - 1
}

Bubble.prototype = new PointXY()

Bubble.prototype.getSize = function (index) {
	var s = this.data.radius ? this._vertAxis.calcSize(this.data.radius[index]) : 0
	this.pointer.width = s
	this.pointer.height = s
}

Bubble.prototype.horizMargins = function (p) {
	this.calcWidth = function (index) {
		this.getSize(index)
		var res = 1 + this.pointer.width * 0.5,
			s = this.pointer.format.stroke
		if (s.fill !== "") res += s.size
		return res
	}

	if (this.pointer.visible && this.inflate) {
		p.x = this.calcWidth(0)
		p.y = this.calcWidth(this.count() - 1)
	}
}

Bubble.prototype.vertMargins = function (p) {
	this.calcHeight = function (index) {
		this.getSize(index)
		var res = 1 + this.pointer.height * 0.5,
			s = this.pointer.format.stroke
		if (s.fill !== "") res += s.size
		return res
	}

	if (this.pointer.visible && this.inflate) {
		var low,
			high,
			lowIndex = 0,
			highIndex = 0,
			l = this.count(),
			pos = { x: 0, y: 0 }

		if (l > 0) {
			this.calc(0, pos)
			low = high = pos.y

			for (var t = 1; t < l; t++) {
				this.calc(t, pos)

				if (pos.y < low) lowIndex = t
				else if (pos.y > high) highIndex = t
			}

			p.x = this.calcHeight(highIndex)
			p.y = this.calcHeight(lowIndex)
		}
	}
}

/**
 * @constructor
 * @augments Bar
 * @class Draws financial Volume data as thin Bar lines.
 */
let Volume = function (o, o2) {
	Bar.call(this, o, o2)

	this.barStyle = "line"
	this.marks.visible = false
	this.colorEach = false
	var f = this.format
	f.shadow.visible = false
	f.gradient.visible = false
	f.stroke.fill = ""
}
Volume.prototype = new Bar()

/**
 * @constructor
 * @augments PointXY
 * @class Draws financial OHLC data as Candle or CandleBar points.
 * @property {String} style Defines candle style ("candle", "bar", "openclose").
 */
let Candle = function (o, o2) {
	PointXY.call(this, o, o2)

	var f = this.format
	f.z = 0.5
	f.depth = 0.1

	this.pointer.width = 7
	this.pointer.format.stroke.visible = false

	var hi = (this.higher = this.pointer.format)
	hi.fill = "green"

	var lo = (this.lower = new Format(this.chart))
	lo.fill = "red"
	lo.stroke.visible = false

	this.style = "candle"

	/*
		* @private
		*/
	this.setChart = (series, chart) => {
		var tmp = Tee.PointXY.prototype.setChart
		tmp(series, chart)
		lo.setChart(chart)
	}

	this.draw = function () {
		var d = this.data,
			len = d.values.length,
			t,
			p = new Point(),
			po = this.pointer,
			w = po.width * 0.5,
			o,
			h,
			l,
			m = this.mandatoryAxis,
			y,
			he,
			c = this.chart.ctx,
			col,
			x,
			r

		c.z = f.z + f.depth * 0.5

		for (t = 0; t < len; t++)
			if (!this.isNull(t)) {
				this.calc(t, p)
				x = p.x

				o = m.calc(d.open[t])
				h = m.calc(d.high[t])
				l = m.calc(d.low[t])

				if (p.y > o) {
					y = o
					he = p.y - o
					col = lo
				} else {
					y = p.y
					he = o - y
					col = hi
				}

				if (this.style == "bar") {
					c.beginPath()

					c.moveTo(x, h)
					c.lineTo(x, l)
					c.moveTo(x - w, o)
					c.lineTo(x, o)
					c.moveTo(x, p.y)
					c.lineTo(x + w, p.y)

					col.stroke.prepare(col.fill)

					c.stroke()
				} else {
					col.depth = w / 100 // totalDepth*0.5 !!
					col.z = f.z + f.depth * 0.5 - col.depth * 0.5

					r = { x: x - w, y: y, width: po.width, height: he }

					if (this.pointer.style === "cylinder") col.cylinder(r, 1, true)
					else col.cube(r)

					col.draw(c, null, r)

					if (this.hover.enabled && this.over == t) this.hover.rectangle(x - w, y, po.width, he)
				}

				if (this.style != "openclose")
					if (h < y || l > y + he) {
						c.z = f.z + f.depth * 0.5

						c.beginPath()

						c.moveTo(x, y)
						c.lineTo(x, h)

						c.moveTo(x, y + he)
						c.lineTo(x, l)

						if (this.hover.enabled && this.over == t) this.hover.stroke.prepare(col.fill)
						else col.stroke.prepare(col.fill)

						c.stroke()
					}
			}
	}

	this.minYValue = function () {
		return this.data.low.length > 0 ? ArrayMin(this.data.low) : 0
	}

	this.maxYValue = function () {
		return this.data.high.length > 0 ? ArrayMax(this.data.high) : 0
	}

	this.addRandom = function (count) {
		var d = this.data
		if (!count) count = 10
		d.values.length = count
		d.close = d.values
		if (d.open) d.open.length = count
		else d.open = new Array(count)
		if (d.high) d.high.length = count
		else d.high = new Array(count)
		if (d.low) d.low.length = count
		else d.low = new Array(count)

		if (count > 0) {
			var tmp = 25 + Math.random() * 100,
				o

			for (var t = 0; t < count; t++) {
				o = d.open[t] = tmp
				tmp = d.close[t] = tmp + Math.random() * 25 - 12.5
				d.high[t] = Math.max(o, tmp) + Math.random() * 15
				d.low[t] = Math.min(o, tmp) - Math.random() * 15
			}
		}
	}
}

Candle.prototype = new PointXY()

Candle.prototype.clicked = function (p) {
	var w = this.pointer.width,
		m = this.mandatoryAxis,
		n = this.notmandatory,
		d = this.data,
		len = d.values.length,
		r = new Rectangle(),
		t,
		o,
		c

	r.width = w

	for (t = 0; t < len; t++)
		if (!this.isNull(t)) {
			r.x = n.calc(t) - w * 0.5
			;(o = m.calc(d.open[t])), (c = m.calc(d.close[t]))
			r.y = o > c ? c : o
			r.height = Math.abs(o - c)

			if (r.contains(p)) return t
		}

	return -1
}

Candle.prototype.vertMargins = () => {}

/**
 * @constructor
 * @augments Tee.CustomSeries
 * @class Draws values as Polar / Radar charts.
 * @property {Number} [rotation = 0] Rotates polar points, from 0 to 360 degree.
 * @property {Boolean} [clockwise = true] Direction of Values.
 */
let Polar = function (o, o2) {
	CustomSeries.call(this, o, o2)

	this.pointer.visible = true
	this.rotation = 0 // degress from 0 to 360

	this._paintAxes = false
	this._paintWalls = false
	this.continuous = false
	this.useOrigin = false
	this.origin = 0

	this.clockwise = true

	var p = { x: 0, y: 0 },
		pp,
		f = this.format,
		center = { x: 0, y: 0 },
		radius,
		pi180 = Math.PI / 180

	f.stroke.fill = "black"
	f.z = 0.5

	this.calc = function (index, p) {
		var d = this.data,
			v = d.values[index],
			mand = this.mandatoryAxis

		var x = d.x ? d.x[index] : (360 * index) / d.values.length,
			dif = mand.inverted ? mand.maximum - v : v - mand.minimum,
			angle = this.clockwise ? pi180 * (this.rotation + x) : -(pi180 * (this.rotation + x)),
			rad = (dif * radius) / (mand.maximum - mand.minimum)

		p.x = center.x + Math.cos(angle) * rad
		p.y = center.y + Math.sin(angle) * rad
	}

	function tryDrawAxis(axis, px, py) {
		if (axis.visible) {
			var old = axis.axisPos
			axis.axisPos = px
			axis.startPos = py - radius
			axis.endPos = py + radius

			var oldz = axis.z
			axis.z = 1 - axis.chart.walls.back.size * 0.5 - 0.1

			axis.drawAxis()

			axis.axisPos = old
			axis.z = oldz
		}
	}

	function calcCenter(r, axis) {
		var rw = r.width,
			rh = r.height
		center.x = r.x + 0.5 * rw
		center.y = r.y + 0.5 * rh
		radius = Math.min(rw, rh) * 0.5

		if (axis.visible && axis.labels.visible) {
			var textH = axis.labels.format.textHeight("W")
			radius -= textH
		}
	}

	this.beforeDraw = function () {
		var oldz

		calcCenter(this.chart.chartRect, this.notmandatory)

		// Background:

		var walls = this.chart.walls

		if (walls.visible) {
			var wall = walls.back

			if (wall.visible) {
				var wallFormat = wall.format

				oldz = wallFormat.z
				wallFormat.z = 1
				wallFormat.ellipse(center.x, center.y, 2 * radius, 2 * radius)
				wallFormat.z = oldz
			}
		}

		if (this.chart.axes.visible) {
			var nomand = this.notmandatory,
				nomandgrid = nomand.grid.format.stroke,
				mand = this.mandatoryAxis,
				vmin = 0,
				ctx = this.chart.ctx,
				angle,
				labelincrem = 10,
				nomandrotation = nomand.rotation || 0

			// Axis Labels

			if (nomand.visible && nomand.labels.visible) {
				var rText = { x: 0, y: 0, width: 0, height: 0 },
					labelsF = nomand.labels.format,
					textH = labelsF.textHeight("W"),
					labelRadius = radius + textH * 0.8

				labelincrem = Math.max(10, 90 / trunc(radius / textH))

				oldz = labelsF.z
				labelsF.z = 0.6

				vmin = 0
				while (vmin < 360) {
					angle = this.clockwise ? pi180 * (vmin + nomandrotation) : -(pi180 * (vmin + nomandrotation))

					rText.x = center.x + Math.cos(angle) * labelRadius
					rText.y = center.y + Math.sin(angle) * labelRadius - textH * 0.5

					labelsF.drawText(rText, "" + vmin)

					vmin += labelincrem
				}

				labelsF.z = oldz
			}

			// line grids

			if (nomand.visible && nomand.grid.visible) {
				var gridincrem = nomand.increment || 10

				ctx.z = 1 - walls.back.size * 0.5 - 0.1
				ctx.beginPath()

				vmin = 0

				while (vmin < 360) {
					angle = pi180 * (vmin + nomandrotation)

					ctx.moveTo(center.x, center.y)
					ctx.lineTo(center.x + Math.cos(angle) * radius, center.y + Math.sin(angle) * radius)

					vmin += gridincrem
				}

				nomandgrid.prepare(nomandgrid.fill, ctx)
				ctx.stroke()
			}

			// circle grids

			if (mand.visible && mand.grid.visible) {
				vmin = mand.roundMin()

				var rad,
					mandgrid = mand.grid.format,
					gridstep = (2 * radius) / (mand.maximum - mand.minimum)

				oldz = mandgrid.z

				mandgrid.z = 1 - walls.back.size * 0.5 - 0.1
				ctx.z = mandgrid.z

				while (vmin < mand.maximum) {
					rad = (vmin - mand.minimum) * gridstep
					mandgrid.ellipse(center.x, center.y, rad, rad)
					vmin += mand.increm
				}

				mandgrid.z = oldz
			}

			tryDrawAxis(mand, center.x, center.y)
			tryDrawAxis(nomand, center.y, center.x)
		}
	}

	this.draw = function () {
		calcCenter(this.chart.chartRect, this.notmandatory)

		var len = this.data.values.length,
			t

		// Draw Points

		pp = []

		for (t = 0; t < len; t++)
			if (!this.isNull(t)) {
				this.calc(t, p)
				pp.push({ x: p.x, y: p.y })
			}

		len = pp.length

		if (len > 0) {
			if (this.style == "bar") {
				var ctx = this.chart.ctx
				ctx.beginPath()

				for (t = 0; t < len; t++) {
					ctx.moveTo(center.x, center.y)
					ctx.lineTo(pp[t].x, pp[t].y)
				}

				this.format.stroke.prepare(this.format.fill, ctx)
				ctx.stroke()
			} else f.polygon(pp)

			if (this.pointer.visible) this.drawPointers()
		}
	}

	this.minYValue = function () {
		var v = Tee.Series.prototype.minYValue.call(this)
		return this.useOrigin ? Math.min(v, this.origin) : v
	}

	this.maxYValue = function () {
		var v = this.stackMaxValue()
		return this.useOrigin ? Math.max(v, this.origin) : v
	}
}

Polar.prototype = new CustomSeries()

/**
 * @constructor
 * @augments Series
 * @class Calculates each point color using point values and the palette colors array.
 */
let PaletteSeries = function (o, o2) {
	Series.call(this, o, o2)

	var palette = this.palette
	palette.colors = RainbowPalette()

	var rgb, numcolors

	this._min = 0
	this._range = 0

	this.prepareColors = () => {
		var p = palette.colors,
			color

		numcolors = p.length
		rgb = new Array(numcolors)

		for (var c = 0; c < numcolors; c++) {
			color = p[c].trim()

			if (color.length == 7)
				// #RRGGBB
				rgb[c] = {
					r: parseInt(color.substr(1, 2), 16),
					g: parseInt(color.substr(3, 2), 16),
					b: parseInt(color.substr(5, 2), 16),
					a: 0
				}
			else if (color.substr(0, 4) == "rgb(") {
				var tmp = color.slice(4, color.length - 1).split(",")
				rgb[c] = { r: tmp[0], g: tmp[1], b: tmp[2], a: tmp[3] || 0 }
			}
		}
	}

	this.getColor = function (value) {
		var colorIndex = ((numcolors - 1) * ((value - this._min) / this._range)) | 0
		return rgb[palette.inverted ? numcolors - 1 - colorIndex : colorIndex]
	}

	/**
	 * @returns {Number} Returns the number of items to show at legend.
	 */
	this.legendCount = function () {
		return this.palette.colors ? this.palette.colors.length : 0
	}
}

PaletteSeries.prototype = new Series()

/**
 * @returns {String} Returns the color of index'th legend symbol.
 */
PaletteSeries.prototype.legendColor = function (index) {
	var p = this.palette,
		c = p.colors,
		i = p.inverted

	if (this.chart.legend.inverted) i = !i

	if (p.grayScale) {
		var tmp = ((index * 255) / c.length) | 0
		if (i) tmp = 255 - tmp
		return "rgb(" + tmp + "," + tmp + "," + tmp + ")"
	} else return c ? c[i ? index : this.legendCount() - index - 1] : this.format.fill
}

/**
 * @returns {String} Returns the palette value of index'th legend symbol.
 */
PaletteSeries.prototype.legendText = function (index /*,style,title,asArray*/) {
	index = -1 + this.legendCount() - index
	return (this._min + (index * this._range) / (this.palette.colors.length - 1)).toFixed(this.decimals)
}

/**
 * @constructor
 * @augments Tee.Series
 * plots a Sliced Treemap
 * @class plots a sliced Treemap.
 */
let Sliced = function (o, o2) {
	//1r algortime de treemap
	Series.call(this, o, o2)

	this.useAxes = false
	this.colorEach = "yes"

	this.draw = function () {
		if (this.data && this.data.values && this.data.labels) {
			var ctx = this.chart.ctx

			this.calcColorEach()

			var values = this.data.values
			var labels = this.data.labels
			var total = values.reduce((total, value) => total + value, 0)

			var sortedMap = values
				.map((value, index) => ({
					value: value,
					label: labels[index]
				}))
				.sort((a, b) => b.value - a.value)

			var y = 0
			var canvasHeight = this.chart.chartRect.height
			var canvasWidth = this.chart.chartRect.width

			for (var i = 0; i < sortedMap.length; i++) {
				var item = sortedMap[i]
				var percentage = item.value / total
				var mida_corresponent = percentage * canvasHeight

				ctx.beginPath()
				ctx.fillStyle = this.getFill(i, this.format)
				ctx.fillRect(0, y, canvasWidth, y + mida_corresponent)

				ctx.strokeStyle = "black"
				ctx.lineWidth = 2
				ctx.strokeRect(0, y, canvasWidth, y + mida_corresponent)

				ctx.closePath()

				ctx.fillStyle = "white"
				ctx.font = "16px Arial"
				ctx.textAlign = "center"
				ctx.textBaseline = "middle"
				ctx.fillText(item.label, canvasWidth / 2, y + mida_corresponent / 2)

				ctx.stroke()

				y += mida_corresponent
			}
		}
	}
}

Sliced.prototype = new Series()

/**
 * @constructor
 * @augments Tee.Series
 * @plots a Treemap
 * @class plots a squarified Treemap.
 */
let Treemap = function (o, o2) {
	Series.call(this, o, o2)

	var f = this.format
	f.stroke.fill = "black"
	f.font.style = "16px Arial"

	this.fillStyle = "white"
	this.lineWidth = 2

	this.palette = new Palette([
		"#4466a3",
		"#f39c35",
		"#f14c14",
		"#4e97a8",
		"#2b406b",
		"#1d7b63",
		"#b3080e",
		"#f2c05d",
		"#5db79e",
		"#707070",
		"#f3ea8d",
		"#b4b4b4"
	])

	this.useAxes = false

	this.drawNode = function (ctx, node, x, y, width, height, n) {
		if (!node || node.length === 0) {
			return
		}
		const children = node.children

		let posy = y
		let xoffset = x

		ctx.beginPath()
		ctx.fillStyle = this.palette.get(n)
		ctx.fillRect(xoffset, posy, width - xoffset, height - posy)
		ctx.strokeStyle = f.stroke.fill
		ctx.lineWidth = this.lineWidth
		ctx.strokeRect(xoffset, posy, width - xoffset, height - posy)
		ctx.fillStyle = this.fillStyle
		ctx.font = f.font.style
		ctx.textAlign = this.format.textAlign
		ctx.textBaseline = "middle"
		if (this.marks.visible) {
			if (ctx.textAlign == "center") {
				ctx.fillText(node.name, x + width / 2, posy + 10)
			}
		} else if (ctx.textAlign == "left") {
			ctx.fillText(node.name, x, posy + 10)
		} else if (ctx.textAlign == "right") {
			ctx.fillText(node.name, width - x, posy + 10)
		}

		if (children == null) return

		const childHeight = (height - posy - 10 * 2) / children.length
		posy += 10
		xoffset += 10

		for (const child of children) {
			this.drawNode(ctx, child, xoffset, posy, width - xoffset, childHeight + posy, ++n)
			posy += childHeight
		}
	}

	this.draw = function () {
		var ctx = this.chart.ctx

		var root = o

		var y = 0
		var canvasHeight = this.chart.chartRect.height
		var canvasWidth = this.chart.chartRect.width

		this.drawNode(ctx, root, 0, y, canvasWidth, canvasHeight, 0)
	}
}

Treemap.prototype = new Series()

/**
 * @constructor
 * @augments Tee.Series
 * @class Draws a Map consisting of any array of polygons.
 * @property {MapItem[]} items The array of map polygons ( points:[[x,y]], format:Tee.Format ).
 */
let Map = function(o,o2) {
  PaletteSeries.call(this,o,o2);

  var map=this.svg=null;

  this.palette.colors = null;
  this.useColors = true;

  // Internal. Flag series to recalc min/max even if empty.
  this.__alwaysDraw=true;

  // Internal. For Axis calcIncrement:
  this.sequential=false;

  this.format.stroke.fill="black";

  this.hover.enabled=true;
  this.hover.transparency=0.5;

  // 3D polygons:
  this.enableDepth = false;

  // 3D depth based on polygon value:
  this.variableDepth = true;

  //this.format.shadow.visible=true;
  
  this.items=[];

  this.setMap=function(svgMap) {
    this.svg=svgMap;
    map=svgMap;
    
    this.items=[];
	this.texts=[];

    if (this.notmandatory)
       this.notmandatory.labels.labelStyle="value";

    var p, t, s=map.svg, l=s.childElementCount, svgpo, po, svgtxts;

    if (l>0) {
       s=s.childNodes[0];
       l=s.childElementCount;
    }

    var item, pItem, tItem;

    for (p=0; p<l; p++) {

      item = s.childNodes[p];
	  
	  if (item.points !== undefined)
	  {
		  svgpo = item.points;

		  po=[];

		  for (t=0; t<svgpo.numberOfItems; t++) {
			pItem = svgpo.getItem(t);
			po.push( {x:pItem.x, y:pItem.y } );
		  }

		  this.items.push({ p:po, item:item });
      }
	  else if (item.nodeName=="text")
	  {
		  po=[];
		  var xVal = parseFloat(item.attributes["x"].value);
		  var yVal = parseFloat(item.attributes["y"].value);
		  po.push( {x:xVal, y:yVal, item:item } );
		  this.items.push({ p:po, item:item });
		  
      }
	  else if (item.nodeName=="ellipse")
	  {
		  po=[];
		  var xVal = parseFloat(item.attributes["cx"].value);
		  var yVal = parseFloat(item.attributes["cy"].value);
		  //var xRad = parseFloat(item.attributes["rx"].value);
		  //var yRad = parseFloat(item.attributes["ry"].value);
		  po.push( {x:xVal, y:yVal, item:item } );
		  this.items.push({ p:po, item:item });
	  }
	  
	  if (item.nodeName=="path")
	  {
		  po=[];
		  var xVal = 0;
		  var yVal = 0;
		  //var xRad = parseFloat(item.attributes["rx"].value);
		  //var yRad = parseFloat(item.attributes["ry"].value);
		  po.push( {x:xVal, y:yVal, item:item } );
		  this.items.push({ p:po, item:item });
	  }	  
    }
  }

  this.minXValue=function() { return map.bounds.x }
  this.minYValue=function() { return map.bounds.y-map.bounds.height }
  this.maxXValue=function() { return map.bounds.x+map.bounds.width }
  this.maxYValue=function() { return map.bounds.y }

  this.transformMap=function(c) {
    var mand = this.mandatoryAxis,
        nomand = this.notmandatory,
        rx= nomand.calc(map.bounds.x),
        ry= mand.calc(map.bounds.y+map.bounds.height),
        xs= nomand.scale,
        ys= mand.scale;

    if (c.__webgl) {
      this.visual=c.beginParent();

      var cr = this.chart.chartRect;

//      this.visual.position.set(rx+-map.size.x -0.5*cr.width, -0.5*cr.height-0.5*map.size.height+map.size.offy+ry, 0); //map.size.height+map.size.y, 0);

      this.visual.position.set(rx - 0.5*cr.width, mand.calc(map.bounds.y) - 0.5*map.size.y + 0.5*cr.height - 0.5*map.size.offy, 0);
      this.visual.scale.set( xs*map.bounds.width/map.size.width, ys*map.bounds.height/map.size.height,1);

      c.z=this.format.z+this.format.depth*0.5;
    }
    else {
      c.translate(rx,ry);
      c.scale( xs*map.bounds.width/map.size.width, ys*map.bounds.height/map.size.height);
      c.translate(-map.size.x-map.size.offx, map.size.height-map.size.y-map.size.offy);
    }
  }

  this.markText=function(index) {
    return this.svg.labels[this.items[index].item.id];
  }
  
  this.draw=function() {

    if (!this.items) return;

    var p,
        l=this.items.length,
        //po, pitem, t,
        pi, f,
        c=this.chart.ctx;

    c.save();

    this.transformMap(c);

    if (this.enableDepth)
       c.depth = this.format.depth * 0.03;
    else
       c.depth = 0;

    //c.beveled=true;

    var oldz = c.z;

    var calcDepth = this.enableDepth && this.variableDepth && (this._range>0),
        tmpFactor = this.format.depth * 0.03 / this._range;

    for (p=0; p<l; p++) {
      pi=this.items[p];

      f=pi.format || this.format;

	  if ((pi.item.localName=="polygon") || (pi.item.localName=="ellipse"))
	  {
		  if (pi.item.style.stroke != "")
			  f.stroke.fill = pi.item.style.stroke;
		  if (pi.item.style.strokeWidth != "")
			  f.stroke.size = parseFloat(pi.item.style.strokeWidth, 10);

      if (this.useColors)
         f.fill=pi.item.style.fill;

      if (calcDepth) {
         c.depth = tmpFactor * this.svg.values[ pi.item.id ];
         c.z = oldz;
      }

	  if (pi.item.localName=="polygon")
	  {
		  if (c.__webgl)
			 c.polygon(pi.p, f);
		  else
			 f.polygon(pi.p);
	  }
	  else
		f.ellipse(pi.p[0].x, pi.p[0].y, parseFloat(pi.item.attributes["rx"].value), parseFloat(pi.item.attributes["ry"].value), 0, 0, 7, false);

	  }
	  else if (pi.item.localName=="path")
	  {
		  var po=[];
		  po.push( {x:0, y:0 } );
		  var instructions=pi.item.attributes[0].value.split(";");
		  
	      var thePath = "";
		  let p = new Path2D(instructions);
          c.stroke(p);
	  }	  
	  else if (pi.item.localName=="text")
	  {
		  c.font = pi.item.attributes["font-family"].value;
		  var strOut = pi.item.attributes["textContent"].value;
		  var rect = { x: pi.p[0].x, y: pi.p[0].y, width: f.textWidth(strOut), height: 0};
		  
		  f.drawText(rect, strOut);
	  }
    }

    var h=this.hover;

    if ((!c.__webgl) && h.enabled && (this.over != -1) ) {
      pi=this.items[this.over];

      var old=h.fill;
      h.fill=pi.item.style.fill;
      h.polygon(pi.p);
      h.fill=old;
    }

    c.restore();

    if (c.__webgl)
      c.endParent();
  }

  this.applyPalette=function(colors) {
    var p=this.palette;
    p.colors=colors || this.chart.palette.colors;
    this.svg.applyPalette(p.colors);

    var tmp, l=this.items.length, t, min, max;

    for (t=0; t<l; t++) {
       tmp = this.svg.values[ this.items[t].item.id ];

       if ((t===0) || (tmp>max))
          max = tmp;

       if ((t===0) || (tmp<min))
          min = tmp;
    }

    this._range = max - min;
    this._min=min;
    this._max=max;

    this.prepareColors();
  }

  function pointInPolygon(ctx, p, points) {

     var t, l=points.length;

     ctx.beginPath();
     ctx.moveTo(points[0].x,points[0].y);

     for (t=1; t<l; t++)
       ctx.lineTo(points[t].x,points[t].y);

     ctx.closePath();

     return ctx.isPointInPath(p.x, p.y);
  }

  this.clicked=function(p) {
    var res=-1, t, l=this.items.length, c=this.chart.ctx;

    //IE8 ExCanvas does not support "isPointInPath"
    if (c.isPointInPath) {

       c.save();
       this.transformMap(c);

       if ((this.over!=-1) && (this.over<l) && pointInPolygon(c, p, this.items[this.over].p))
         res=this.over;
       else
       for (t=l-1; t>=0; t--)
           if (pointInPolygon(c, p, this.items[t].p)) {
              res=t;
              break;
           }

       c.restore();
    }

    return res;
  }

  this.getItemBounds=function(item) {
    // Cache bounds calculation:

    if (!item.bounds) {
      item.bounds = { x:0, y:0, width:0, height:0 }
      this.format.polygonBounds(item.p, item.bounds);
    }

    return item.bounds;
  }

  // Return xy pixel position of index'th polygon bounding center:

  this.calc=function(index, p) {
    var item = this.items[index], mand=this.mandatoryAxis;

    var b=this.getItemBounds(item);

    p.x = this.notmandatory.calc(b.x + (b.width*0.5) ),
    p.y = mand.startPos + mand.endPos - mand.calc( b.y + (b.height*0.5) );
  }

  function parseCoordinate(coord, invert) {
    if (typeof coord==='string') {
       var i = coord.indexOf(' ');
       if (i>0) {
         var tmp = coord.substr(i+1,1);
         coord = parseFloat(coord.substr(0,i));
         if (tmp===invert)
            coord = -coord;
       }
    }
    return coord;
  }

  this.addLocation=function(series, lat, lon, text) {
    var data=series.data;

    if (!data.x)
       data.x=[];

    lat = parseCoordinate(lat,'S');
    lon = -parseCoordinate(lon,'E');

    data.values.push( lat );
    data.x.push(lon);
    data.labels.push( text || "" );
  }
  
  this.addPoint=function(series, x, y, text) {
    var data=series.data;

    if (!data.x)
       data.x=[];

    y = parseCoordinate(y,'Y');
    x = parseCoordinate(x,'X');

    data.values.push( y );
    data.x.push(x);
    data.labels.push( text || "" );
  }
}

Map.prototype = new PaletteSeries();

/**
 * @constructor
 * @augments Tee.PaletteSeries
 * @class Draws a two dimensional grid of data as a "grid" (rows and columns of pixels),
 * using the palette property to calculate each pixel (grid cell) color.
 */
let ColorGrid=function(o,o2) {
  PaletteSeries.call(this,o,o2);

  this.smooth=true;
  this.dataChanged=true;

  var d, width, height, image, data, c2, ctx2, axis, axis2,
      x0, y0, x1, y1,
      min, max, range, InvRange255;

  function optimize(smooth, style, ctx) {
    if (style)
    {
      // -webkit-optimize-contrast, (-o)(-moz)crisp-edges

      if (style.getPropertyValue("image-rendering") !== null)
         style.setProperty ("image-rendering", smooth ? "optimizeQuality" : "optimizeSpeed", null);

      if (style.msInterpolationMode !== undefined)
         style.msInterpolationMode = smooth ? "bicubic" : "nearest-neighbor";
    }

    ctx.imageSmoothingEnabled=smooth;
  }

  function square(x) { return x*x; }

  this.addRandom=function(rows,cols) {
    var row, temp;

    rows=rows || 200;
    cols=cols || rows;

    var d=this.data.values=[];

    for (var y=0; y<rows; y++) {
      row=new Array(cols);
      d[y]=row;

      // Some sample function
      temp = 0.5 * square(Math.cos(y/(cols*0.2)));

      for (var x=0; x<cols; x++)
           row[x] = square(Math.cos(x/(rows*0.2))) - Math.cos(x/(cols*0.5)) + temp;
    }

    this.dataChanged=true; // <-- force recalculating pixel colors
  }
  
  function tryDrawGrid(s, c) {
    if (s.fill!=="") {
      var p, x, y, xInc=1, yInc=1;

      xInc=Math.max( ( 1+(4*width/Math.abs(axis2.calc(width)-axis2.calc(0)))) |0, 1);
      yInc=Math.max( ( 1+(4*height/Math.abs(axis.calc(height)-axis.calc(0)))) |0, 1);

      c.beginPath();

      for(x=1; x<width; x+=xInc) {
        p=axis2.calc(x-0.5);
        c.moveTo(p, y1);
        c.lineTo(p, y0);
      }

      for(y=1; y<height; y+=yInc) {
        p=axis.calc(y-0.5);
        c.moveTo(x0, p);
        c.lineTo(x1, p);
      }

      s.prepare();
      c.stroke();
    }
  }

  function calcMinMax() {
    min=d[0][0];
    max=min;

    var v, row;

    for(var y=0; y<height; y++) {
      row=d[y];
      for(var x=0; x<width; x++) {
        v=row[x];
        if (v<min) min=v; else
        if (v>max) max=v;
      }
    }

    range=max-min;

    InvRange255= (max==min) ? 1 : 255/(max-min);
  }

  this.fillPixels=function(alpha) {
    var index=0, row, color, gray=this.palette.grayScale,
        inv=this.palette.inverted;

    // Fill pixel data

    for(var y=0; y<height; y++) {
      row=d[y];

      for(var x=0; x<width; x++) {

        if (gray) {
          color = InvRange255 * (inv ? (max - row[x]) : (row[x] - min));

          data[index++] = color;
          data[index++] = color;
          data[index++] = color;
        }
        else {
          color=this.getColor(row[x]);

          data[index++] = color.r;
          data[index++] = color.g;
          data[index++] = color.b;
        }

        data[index++] = alpha;
      }
    }
  }

  this.draw=function() {

    var c=this.chart.ctx;

    if (width>0) {

      if ( (!image) || (c2.width!=width) || (c2.height!=height) ){

        if (!c2)
            c2= document.createElement("canvas");

        // IE <9
        if ( (!c2) || (! c2.getContext) ) return;
        
        c2.width=width;
        c2.height=height;

        ctx2=c2.getContext("2d");
        image = ctx2.getImageData(0,0,width, height);
        data=image.data;
      }

      if (this.dataChanged) {
        this.fillPixels(255*(1-this.format.transparency));
        this.dataChanged=false;
      }

      ctx2.putImageData(image, 0,0);

      optimize(this.smooth, this.chart.canvas.style, c);

      axis=this.mandatoryAxis;
      axis2=this.notmandatory;

      y1=axis.calc(-0.5);
      y0=axis.calc(height-0.5),

      x0=axis2.calc(-0.5);
      x1=axis2.calc(width-0.5);

      var cr=this.chart.chartRect, xPos=x0, yPos=y0, xScale=1, yScale=1;

      if (axis.inverted) {
         yScale=-1;
         yPos=-y1;
      }

      if (axis2.inverted) {
         xScale=-1;
		 xPos=-x1; // #2120
      }

      c.scale(xScale,yScale);
      c.drawImage(ctx2.canvas, 0,0,width,height, xPos,yPos, x1-x0, y1-y0);
      c.setTransform(1, 0, 0, 1, 0, 0);

      tryDrawGrid(this.format.stroke, c);
    }
  }

  /**
  * @returns {String} Returns the palette value of index'th legend symbol.
  */
  this.valueText=function(index) {
    var x=index % width, y=(index/width)|0;
    return (this.data.values[y][x]).toFixed(this.decimals);
  }

  this.minXValue=function() { return -0.5; }
  this.maxXValue=function() { return width-0.5; }
  this.minYValue=function() { return -0.5; }
  this.maxYValue=function() { return height-0.5; }

  this.recalcAxes=function() {
    Tee.Series.prototype.recalcAxes.call(this);

    d=this.data.values;
    height=d.length;
    width=height>0? d[0].length : 0;

    this.size={x:width, y:height};
    
    if ((width>0) && this.dataChanged) {
       calcMinMax();

       this._min=min;
       this._max=max;
       this._range=range;

       this.prepareColors();
    }
  }

 /**
  * @returns {Number} Returns the index of grid xy that contains {@link Tee.Point} p parameter.
  */
  this.clicked=function(p) {

    var x,y;

    if ((p.x>=x0) && (p.x<=x1)) {
      x=(width*(p.x-x0)/(x1-x0)) | 0;

      if ((p.y>=y0) && (p.y<=y1)) {
        y=(height*(p.y-y0)/(y1-y0)) | 0;

        return (y*width) + x;
      }
    }

    return -1;
  }

}

ColorGrid.prototype=new PaletteSeries;


/**
 * @constructor
 * @augments Tee.PaletteSeries
 * @class Draws a two dimensional grid of data as a "grid" (rows and columns of pixels),
 * using the palette property to calculate each pixel (grid cell) color.
 */
let Surface = function(o,o2) {
  ColorGrid.call(this,o,o2);

  var d, _sizeY;

  this.grid=new Tee.Format(o).stroke;

  this.wireFrame=false;
  this.maxZ=3;

  this.getY=function(x,z) { return d[x][z]; }

  this.draw=function() {

    if ((this.size.x>0) && (this.size.y>0)) {

      d=this.data.values;

      if (this.chart.__webgl) {
        var r={};
        this.bounds(r);

        _sizeY=this.size.y-1;
        this.chart.ctx.surface(this.size, this.getY, r, this.wireFrame, this.grid.visible ? this.grid.fill : null, this.maxZ);
      }
    }
  }

  this.minXValue=function() { return 0; }
  this.maxXValue=function() { return this.size.y; }
  this.minYValue=function() { return this._min; }
  this.maxYValue=function() { return this._max; }
}

Surface.prototype = new ColorGrid;

export { ActivityGauge, Area, Bar, Bubble, Bullet, Candle, ColorGrid, CustomBar, CustomSeries, Donut, Gantt, HighLowBar, HorizArea, HorizBar, Line, Map, PaletteSeries, Pie, PointXY, Polar, Series, SeriesList, Sliced, Surface, Treemap, Volume }

